import { ArrowDownOutlined, LoadingOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Divider,
  Dropdown,
  Flex,
  Grid,
  Image,
  Input,
  List,
  message,
  Popover,
  Row,
  Space,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from "antd";

import dayjs from "dayjs";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useNavigation, useParams, useSearchParams } from "react-router-dom";
import { useDebounce, useDebouncedCallback } from "use-debounce";
import "../assets/css/conversationPage.css";
import "../assets/css/outbound.css";
import MediaError from "../assets/images/mediaError.jpg";
import ComponentPreview from "../components/custom/ComponentPreview";
import Drawer from "../components/custom/drawer/Drawer";
import { default as HButton, default as HSButton } from "../components/custom/input/button/Button";
import HSelect from "../components/custom/input/select/Select";
import NewConversationModal from "../components/custom/NewConversationModal";
import HNotification from "../components/custom/notification/HNotification";
import ReusableFilePreview from "../components/custom/ReusableFilePreview";
import CommonMessageTextArea from "../components/main/CommonMessageTextArea";
import ImportContactsNotification from "../components/main/ImportContactsInfo";
import ConversationStatusSelector from "../components/main/InboxContactInfo/ConversationOpenClosed";
import Notes from "../components/main/InboxContactInfo/Notes";
import ContactInfoPanel from "../components/main/InboxContactInfo/Panel";
import DateAndStatus from "../components/main/InboxDetailView/DateAndStatus";
import InboxFilterPopoverCard from "../components/main/InboxListView/InboxFilterPopoverCard";
import SendMessageButton from "../components/main/SendMessageButton";
import { CREDIT_BASED_SERVICES, INBOX_DEFAULT_FILTERS, INBOX_DEFAULT_FILTERS_VALUES, urlParams, WHATSAPP_DISPLAY_NAME } from "../constants/AppConstants";
import { INFO_MESSAGES } from "../constants/messages/en_US";
import { ProviderCommonObj, ProviderExtendedCommonObj } from "../constants/ProviderConstants";
import { AppContext } from "../context/AppContext";
import { usePusher } from "../context/PusherContext";
import { useRolesContext } from "../context/RolesContext";
import useAttachment from "../hooks/useAttachment";
import useContacts from "../hooks/useContacts";
import useHttp from "../hooks/useHttp";
import useLicense from "../hooks/useLicense";
import useUsers from "../hooks/useUsers";
import UserProfileCard from "../voice/components/common/UserProfileCard";
import { useVoiceTeams } from "../voice/hooks/useVoiceTeams";
import ConversationAvatar from "./ConversationAvatar";
import { ConvContext, DEFAULT_CONTACT_ID } from "./ConversationContext";
import { useListViewStore } from "./listViewStore";
import { MessageDirection } from "./MessageUtils";
import {
  promptForNormalGreetingMessage,
  promptForRephraseMessage,
  promptForRephraseMessageBasedOnConversation,
  promptForSuggestReply,
} from "./utils/AiPrompt";
import { successNotification } from "./utils/CommonNotifications";
import {
  checkCurrentUserHasAccountOwnerRolePermission,
  checkTruthy,
  checkUserHasEditAccess,
  checkUserHasModuleAccess,
  deselectCurrentContact,
  findServiceInfoByServiceId,
  formatPhoneNumber,
  formatTime,
  getHighUsageAlertMessage,
  getSettingBySettingName,
  mapToMediaList,
  openServiceNotAuthorizedModal,
  PhoneNumberAccessEnum
} from "./utils/commonUtils";
import {
  BuyFunds,
  ConfigurePhone,
  ConversationListSkeletonLoading,
  DropOverlay,
  findServiceById,
  FromNumberComponent,
  getContactAvatar,
  getDisplayNameOfCustomer,
  MessageAreaInfo,
  SendSMSToNumberComponent
} from "./utils/CommonVessels";
import ImportChat from "./utils/ImportChat";
import { useVoiceIntegId } from "../voice/utils/VoiceUtils";

const { Text } = Typography;
const clean = (obj) => {
  try {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));
  } catch (e) {
    console.error("Error in clean():", e);
    return {};
  }
};

const CONVERSATION_STATUS_UPDATES_INBOX_STATUSES = ["conversation_closed", "conversation_opened", "conversation_reopened", "conversation_reopened_by_system"];

const getFlagColors = (size = "medium") => {
  const FLAG_COLORS = [
    {
      key: "red",
      color: "red",
      outlineIcon: (
        <div
          data-cy="flag-icon-red"
          className={`actionIconsSprite2 ${size === "large" ? "flag-icon-large" : "flag-icon"} flag-icon-red-outlined${size === "large" ? "-large" : ""
            }`}
        />
      ),
    },
    {
      key: "orange",
      color: "orange",
      outlineIcon: (
        <div
          data-cy="flag-icon-orange"
          className={`actionIconsSprite2 ${size === "large" ? "flag-icon-large" : "flag-icon"} flag-icon-orange-outlined${size === "large" ? "-large" : ""
            }`}
        />
      ),
    },
    {
      key: "yellow",
      color: "yellow",
      outlineIcon: (
        <div
          data-cy="flag-icon-yellow"
          className={`actionIconsSprite2 ${size === "large" ? "flag-icon-large" : "flag-icon"} flag-icon-yellow-outlined${size === "large" ? "-large" : ""
            }`}
        />
      ),
    },
    {
      key: "green",
      color: "green",
      outlineIcon: (
        <div
          data-cy="flag-icon-green"
          className={`actionIconsSprite2 ${size === "large" ? "flag-icon-large" : "flag-icon"} flag-icon-green-outlined${size === "large" ? "-large" : ""
            }`}
        />
      ),
    },
  ];
  return FLAG_COLORS;
};
// FlagSelector Component
const FlagSelector = ({ contactId, currentFlag, onFlagChange, appProperties, size = "medium", disabled = false }) => {
  // Flag colors configuration

  const { fetchData } = useHttp();
  const [isLoading, setIsLoading] = useState(false);

  const handleFlagSelect = async (flagColor) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      // If clicking the same flag or explicitly clearing (empty string), remove it
      const isRemoving = flagColor === currentFlag || flagColor === "";
      const flagParam = isRemoving ? "" : flagColor;
      const endpoint = `omessage/contact/${contactId}/flag?flag=${flagParam}`;

      const updateFlagResponse = await fetchData(endpoint, "POST", null, appProperties);
      if (updateFlagResponse) {
        const updateFlagResponseData = JSON.parse(updateFlagResponse);
        if (updateFlagResponseData?.data?.success) {
          onFlagChange(isRemoving ? null : flagColor);
          // Flag updated successfully - no conversation history needed
        } else {
          throw new Error(typeof updateFlagResponseData?.data === "string" ? updateFlagResponseData?.data : null);
        }
      }
      // Update the flag locally

      // successNotification(
      //   isRemoving
      //     ? "Flag removed successfully"
      //     : "Flag set successfully"
      // );
    } catch (error) {
      console.error("Error updating flag:", error);
      HNotification.notify({ type: "error", message: "Error", description: error?.message || "Failed to update flag" });
    } finally {
      setIsLoading(false);
    }
  };
  const FLAG_COLORS = getFlagColors(size);
  const menuItems = FLAG_COLORS.map((flag) => ({
    key: flag.key,
    label: (
      <div className="d-flex justify-content-between align-items-center py-1 px-2 hs-min-w-50-px cursor-pointer">
        {flag.outlineIcon}
        <div>
          {currentFlag === flag.key && (
            <span>
              <div class="actionIconsSprite success-Flag-icon"></div>
            </span>
          )}
        </div>
      </div>
    ),
    onClick: () => handleFlagSelect(flag.key),
  }));

  const currentFlagData = FLAG_COLORS.find((f) => f.key === currentFlag);

  return (
    <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight" disabled={isLoading || disabled} overlayClassName="flag-dropdown">
      <div className={`cursor-pointer d-flex align-items-center`} style={{ opacity: isLoading ? 0.5 : 1 }}>
        {currentFlag ? (
          <Tooltip title={currentFlagData?.key}>{currentFlagData?.outlineIcon}</Tooltip>
        ) : (
          <div className="d-flex justify-content-center">
            <div
              className={`actionIconsSprite2 ${size === "large" ? "flag-icon-large" : "flag-icon"} ${
                disabled ? "hs-cursor-not-allowed" : ""
              } empty-flag-icon${size === "large" ? "-large" : ""}`}
            />
          </div>
        )}
      </div>
    </Dropdown>
  );
};

function ConversationView(props) {
    const {
        chatView,
        toNumber,
        setHelloSend,
        fromSendSMSPage,
        phoneFieldConfiguration,
        setPhoneFieldConfiguration,
        setToNumberNumberInFormView,
        onConversationsLoaded,
    } = props;
    const { lg: greaterThanLargeScreen } = Grid.useBreakpoint();
    const [appProperties, setAppProperties] = useContext(AppContext);
    const [rolesProperties] = useRolesContext();
    const { channel, sendsms, selectedContactId, conversationContext, setConversationContext } = useContext(ConvContext);
    const { data: numberList, setData: setNumbersList } = useListViewStore();
    const { contactId: conatctIdFromUrl } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDescription, setShowDescription] = useState(true);
    const [selectedContact, setSelectedContact] = useState(null);
    const [filterContact, setFilterContact] = useState("");
    const [services, setServices] = useState([]);
    const [showConversations, setShowConversations] = useState(false);
    const [isChannelPresent, setIsChannelPresent] = useState(services?.length > 0 ? true : false);
    const [conversationClassName, setConversationClassName] = useState();
    const [showChatOverlay, setShowChatOverlay] = useState(false);
    const [showModalOverChat, setShowModalOverChat] = useState(false);
    const [importContactsNotificationOpen, setImportContactsNotificationOpen] = useState(false);
    const [contactsAvailable, setContactsAvailable] = useState(null);
    const [showContactInfo, setShowContactInfo] = useState(false);
    const [showSwitch, setShowSwitch] = useState(true);
    const [showChatView, setShowChatView] = useState(false);
    const { getVoiceTeams, getCurrentUserAssociatedTeams } = useVoiceTeams();
    const [newFromNumber, setNewFromNumber] = useState();
    const [selectedService, setSelectedService] = useState(null);
    const [showNotes, setShowNotes] = useState(false);
    const [noteIdToBeFocused, setNoteIdToBeFocused] = useState(null);
    const [contactsLoaded, setContactsLoaded] = useState(false);
    const voiceIntegId = useVoiceIntegId(appProperties) || "";

  useEffect(() => {
    if (greaterThanLargeScreen) {
      setShowContactInfo(true);
    } else {
      setShowContactInfo(false);
    }
  }, [greaterThanLargeScreen]);

  const toggleShowContactInfo = () => {
    setShowContactInfo((prev) => !prev);
  };

  const handleFocusNote = (currentNoteObject) => {
    if (chatView) {
      setShowNotes(true);
    } else {
      setShowContactInfo(true);
    }

    setNoteIdToBeFocused(currentNoteObject?.id?.split(":::")[0]);
  };

  const { subscribe, unSubscribe } = usePusher();
  const selectedServiceRef = useRef(null);
  const newToNumberInputRef = useRef();

  useEffect(() => {
    if (fromSendSMSPage) {
      setConversationClassName("conversationInbox hs-h-92");
    } else {
      setConversationClassName("conversationInbox");
    }
  }, [fromSendSMSPage]);

  useEffect(() => {
    if (services?.length > 0 || !appProperties.installedApps || chatView) return;

    const hasAssociatedNumbers = (teamId) => appProperties?.allNumbers?.some((n) => n?.associatedTeam === teamId);

    const mapTeams = (teams, isVoiceTeams = false) => {
      return teams
        .map((team) => {
          const entity = isVoiceTeams ? team?.teamsEntity : team;
          if (!entity) return null;

          const { teamId, friendlyName, hasAssociatedSmsNumbers } = entity;
          if (hasAssociatedSmsNumbers || hasAssociatedNumbers(teamId)) {
            return {
              value: `_team___${teamId}`,
              title: friendlyName,
              teamId,
              teamName: friendlyName,
            };
          }
          return null;
        })
        .filter(Boolean);
    };

    const getUserAssociatedTeams = async () => {
        try {
            const voiceTeams = await getCurrentUserAssociatedTeams();
            return mapTeams(voiceTeams);
        } catch (err) {
            console.error("Error fetching user teams:", err);
            return [];
        }
    };

    const getAllTeams = async () => {
        try {
            const voiceTeams = await getVoiceTeams(voiceIntegId);
            return mapTeams(voiceTeams, true);
        } catch (err) {
            console.error("Error fetching all teams:", err);
            return [];
        }
    };

    const buildServices = async () => {
      let teamsData = [];
      if (checkCurrentUserHasAccountOwnerRolePermission(appProperties, rolesProperties)) {
        teamsData = await getAllTeams();
      } else if (checkUserHasModuleAccess(rolesProperties?.currentRoleDetails, "Teams")) {
        teamsData = await getUserAssociatedTeams();
      }

      const currentUserPhoneAccess = appProperties?.currentUser?.phoneAccess;

      let phoneNumbersList = [];
      if (
        currentUserPhoneAccess === PhoneNumberAccessEnum.ASSIGNED_NUMBERS ||
        currentUserPhoneAccess === PhoneNumberAccessEnum.TEAM_ASSIGNED_NUMBERS
      ) {
        phoneNumbersList =
          appProperties?.savedNumbers?.filter(
            (sn) =>
              sn.associatedUsers?.includes(appProperties?.currentUser?.osyncUserId) || sn.osyncUserId === appProperties?.currentUser?.osyncUserId
          ) || [];
      } else {
        phoneNumbersList = appProperties?.savedNumbers || [];
      }

      const servicesResponse = appProperties?.installedApps
        ?.filter((app) => app.right?.serviceType !== "VOICE")
        ?.map((installedApp) => {
          const savedNumbers = phoneNumbersList.filter((sn) => sn.serviceName === installedApp.right?.service_name);

          if (savedNumbers.length === 0) return null;

          return {
            value: installedApp.right?.service_id,
            title: installedApp.right?.service_name === "whatsapp" ? "WABA" : installedApp.right?.service_display_name,
            children: savedNumbers.map((sn) => ({
              value: `_phone___${sn.phoneNumber}`,
              title: sn.friendlyName ? `${formatPhoneNumber(sn.phoneNumber)} (${sn.friendlyName})` : formatPhoneNumber(sn.phoneNumber),
              phoneNumber: sn.phoneNumber,
              friendlyName: sn.friendlyName,
            })),
          };
        })
        .filter(Boolean);

      if (teamsData.length > 0) {
        servicesResponse.push({
          value: "teams",
          title: "Teams",
          children: teamsData,
        });
      }

      setServices(servicesResponse);
    };

    buildServices();
  }, [appProperties?.installedApps, appProperties?.allNumbers, services?.length, getVoiceTeams, chatView, rolesProperties]);

  const handleNumberSelect = useCallback((firstName, selectedNumber, contact) => {
    setIsChannelPresent(true);
    setShowChatOverlay(true);
    setNewFromNumber(selectedNumber?.fromNumber);
    setSelectedContact(selectedNumber);
    var getConversationObject = [];
    if (numberList.length > 0) {
      getConversationObject = numberList?.filter((obj) => obj?.contactId === contact?.contactId);
    }
    
    if (getConversationObject.length < 1 || selectedNumber.new) {
      var idObject = {
        firstName: firstName,
        orgCustomer: selectedNumber?.toNumber,
        avatar: selectedNumber?.avatar,
        sourceModule: selectedNumber?.sourceModule,
        sourceId: selectedNumber?.sourceId,
        contactOwnerEntityId: selectedNumber?.contactOwnerEntityId,
        conversationStatus: contact?.conversationStatus,
        conversationStatusType: contact?.conversationStatusType,
        id: contact?.contactId || contact?.id,
        contactId: contact?.contactId || DEFAULT_CONTACT_ID,
        messageTag: "Start conversation",
        new: "New",
      };
      selectedContactId.current = idObject.contactId;
      setNumbersList((prevNumberList) => [
        { ...idObject },
        ...(prevNumberList || [])?.filter?.((obj) => obj?.contactId !== idObject.contactId)?.filter(Boolean),
      ]);
      setConversationContext((prev) => ({
        ...prev,
        [idObject.contactId]: {
          ...(prev?.[idObject.contactId] || {}),
          firstName: firstName,
          ...idObject,
        },
      }));
    }
    const contactId =  contact?.contactId || DEFAULT_CONTACT_ID;

      // Only navigate if we're not already on this route
      const currentRouteContactId = conatctIdFromUrl;
      if (!chatView && currentRouteContactId !== contactId) {
        navigate({ pathname: `/inbox/${contactId}`, search: location.search }, { replace: true });
      }
  }, [numberList, navigate, location.search, conatctIdFromUrl, setIsChannelPresent, setShowChatOverlay, setNewFromNumber, setSelectedContact, setNumbersList, setConversationContext]);

  useEffect(() => {
    if (!conatctIdFromUrl || conatctIdFromUrl === DEFAULT_CONTACT_ID) {
      return;
    }

    // Set selectedContactId immediately when route changes
    if (selectedContactId.current !== conatctIdFromUrl) {
      selectedContactId.current = conatctIdFromUrl;
    }

    // Wait for numberList to be available
    if (!numberList || numberList.length === 0) {
      return;
    }

    const contactFromList = numberList.find((item) => item.contactId === conatctIdFromUrl);
    
    if (contactFromList) {
      
      // Check if this contact is already selected
      if (
        selectedContactId.current === conatctIdFromUrl &&
        selectedContact?.toNumber === contactFromList.orgCustomer
      ) {
        return; // Already selected, skip
      }

      const selectedNumbers = {
        fromNumber: contactFromList.orgMember,
        toNumber: contactFromList.orgCustomer,
        integId: contactFromList.integId,
      };
      
      handleNumberSelect(
        contactFromList.firstName || contactFromList.lastName || "",
        selectedNumbers,
        contactFromList
      );
    }
  }, [conatctIdFromUrl, numberList, handleNumberSelect, selectedContact]);

  useEffect(() => {
    if (selectedService) {
      selectedServiceRef.current = selectedService;
    }
  }, [selectedService]);

  useEffect(() => {
    if (!chatView) {
      subscribe(
        "CONTACTS_UPDATE",
        (data) => {
          if (data) {
            try {
              let response = JSON.parse(data?.data);
              // Check if the contact has userUnreadCount map and if current user's ID is in it
              if (response?.userUnreadCountMap && appProperties?.currentUser?.osyncUserId) {
                const userSpecificCount = response.userUnreadCountMap[appProperties?.currentUser?.osyncUserId];
                if (userSpecificCount) {
                  response.unreadCount = userSpecificCount;
                }
              }

              const service = findServiceById(appProperties, selectedServiceRef.current);
              if (
                !selectedServiceRef.current ||
                selectedServiceRef.current?.toLowerCase() === "all" ||
                response?.rightServiceId === service?.serviceId ||
                response?.orgMember === selectedServiceRef.current
              ) {
                setNumbersList((prev) => {
                  if (response?.contactId === selectedContactId.current) {
                    response.unreadCount = 0;
                  }

                  const existingIndex = prev?.findIndex((item) => item?.contactId === response?.contactId);
                  if (existingIndex !== -1) {
                    const newList = [...prev];
                    newList[existingIndex] = response;
                    newList.unshift(newList.splice(existingIndex, 1)[0]);
                    return newList;
                  } else {
                    return [response, ...(prev || [])];
                  }
                });
              }
            } catch (e) {
              console.error("ERROR::::", e);
            }
          }
        },
        false,
        true
      );
      return () => {
        unSubscribe("CONTACTS_UPDATE", "ConversationView::167");
      };
    }
  }, [channel, chatView, appProperties?.currentUser?.osyncUserId]);
  useEffect(() => {
    if (!chatView && importContactsNotificationOpen) {
      subscribe(
        "CONTACT_IMPORT_INFO",
        (data) => {
          if (data) {
            try {
              let response = JSON.parse(data?.data);
              if (response?.import_status) {
                setConversationContext((prev) => ({
                  ...prev,
                  importedContacts: {
                    ...(prev?.importedContacts || {}),
                    recordsProcessed: prev?.importedContacts?.totalRecords,
                    import_status: response?.import_status,
                  },
                }));
                return;
              }
              const contact = JSON.parse(response?.contact);
              setNumbersList((prev) => {
                const existingIndex = prev?.findIndex((item) => item?.contactId === contact?.contactId);
                if (existingIndex !== -1) {
                  return prev;
                } else {
                  return [...(prev || []), contact];
                }
              });
              setConversationContext((prev) => ({
                ...prev,
                importedContacts: {
                  ...prev?.importedContacts,
                  recordsProcessed: parseInt(prev?.importedContacts?.recordsProcessed || 0, 10) + 1,
                  totalRecords: parseInt(response?.totalRecords || 0, 10),
                },
              }));
            } catch (e) {
              console.error("ERROR::::", e);
            }
          }
        },
        false,
        true
      );
      return () => {
        unSubscribe("CONTACT_IMPORT_INFO", "ConversationView::217");
      };
    }
  }, [channel, chatView, appProperties?.currentUser?.osyncUserId, importContactsNotificationOpen]);

  useEffect(() => {
    subscribe(
      "CONTACT_READ_STATUS_UPDATE",
      (data) => {
        if (data) {
          try {
            let response = JSON.parse(data?.data);

            setNumbersList((prev) => {
              const existingIndex = prev?.findIndex((item) => item?.contactId === response?.contactId);

              if (existingIndex !== -1) {
                const newList = [...prev];
                const updatedContact = { ...newList[existingIndex] };

                if (response?.userUnreadCountMap && appProperties?.currentUser?.osyncUserId) {
                  const userSpecificCount = response.userUnreadCountMap[appProperties?.currentUser?.osyncUserId];

                  updatedContact.unreadCount = userSpecificCount !== undefined ? userSpecificCount : 0;
                } else if (response?.unreadCount !== undefined) {
                  updatedContact.unreadCount = response.unreadCount;
                } else {
                  updatedContact.unreadCount = 0;
                }

                newList[existingIndex] = updatedContact;
                return newList;
              }

              return prev;
            });
          } catch (e) {
            console.error("Error handling CONTACT_READ_STATUS_UPDATE:", e);
          }
        }
      },
      false,
      true
    );

    return () => {
      unSubscribe("CONTACT_READ_STATUS_UPDATE", "ConversationView::ReadStatus");
    };
  }, [channel, chatView, appProperties?.currentUser?.osyncUserId]);

  useEffect(() => {
    subscribe(
      "CONTACT_INFO_UPDATE",
      (data) => {
        if (data) {
          try {
            let response = JSON.parse(data?.data);

            const eventContactId = response?.contactId;

            if (eventContactId === selectedContactId.current) {
              const messageId = response?.messageId;
              const internalMessageId = response?.internalMessageId;

              setConversationContext((prev) => {
                const existingIndex = prev?.[selectedContactId?.current]?.conversations?.findIndex(
                  (item) => item?.id === messageId || item?.id === internalMessageId
                );

                if (existingIndex >= 0) {
                  const newList = [...(prev[selectedContactId?.current]?.conversations ?? [])];
                  newList[existingIndex] = {
                    ...newList[existingIndex],
                    messageStatus: response?.messageStatus,
                    errorMessage: response?.errorMessage,
                    errorCode: response?.errorCode,
                    class: response?.messageStatus === "failed" ? "input_error" : "",
                  };

                  return {
                    ...prev,
                    [selectedContactId?.current]: {
                      ...prev[selectedContactId?.current],
                      conversations: newList,
                    },
                  };
                }
                return prev;
              });
            }

            if (eventContactId) {
              setNumbersList((prev) => {
                const existingIndex = prev?.findIndex((item) => item?.contactId === eventContactId);
                if (existingIndex !== -1) {
                  const newList = [...prev];
                  newList[existingIndex] = {
                    ...newList[existingIndex],
                    messageTagStatus: response?.messageStatus,
                  };
                  return newList;
                }
                return prev;
              });
            }
          } catch (e) {
            console.error("ERROR handling CONTACT_INFO_UPDATE:", e);
          }
        }
      },
      false,
      true
    );

    return () => {
      unSubscribe("CONTACT_INFO_UPDATE", "ConversationView::CONTACT_INFO_UPDATE");
    };
  }, [channel, chatView, appProperties?.currentUser?.osyncUserId]);

  const showDefaultInbox = () => {
    selectedContactId.current = DEFAULT_CONTACT_ID;
    setSelectedContact(null);
    setShowChatOverlay(true);
    setIsChannelPresent(false);
  };

  const handleNoteUpdate = (entityId, noteData) => {
    if (!entityId) {
      console.warn("entityId is required");
      return;
    }
    setConversationContext((prev) => ({
      ...prev,
      [entityId]: {
        ...(prev?.[entityId] || {}),
        conversations: [...(prev?.[entityId]?.conversations || []), ...(noteData || [])],
      },
    }));
  };

  const handleOpenNotes = (open) => {
    setShowNotes(open);
  };

  const handleCloseNotes = () => {
    setShowNotes(false);
    setNoteIdToBeFocused(null);
  };

  return (
    <>
      <div id="custom-toolbar" className="d-none" />

      {appProperties?.osyncId && (
        <>
          <Drawer styles={{ body: { padding: "0" } }} open={showNotes} setOpen={handleOpenNotes} onClose={handleCloseNotes} title="Notes">
            <Notes
              appProperties={appProperties}
              entityId={selectedContactId.current}
              integId={selectedContact?.integId}
              onNoteAdded={handleNoteUpdate}
              onNoteUpdated={handleNoteUpdate}
              onNoteDeleted={handleNoteUpdate}
              noteIdToBeFocused={noteIdToBeFocused}
              setNoteIdToBeFocused={setNoteIdToBeFocused}
            />
          </Drawer>
          <Row className={`overflow-lg-hidden h-100`} data-cy="inbox-container">
            {!chatView && (
              <Col xs={24} xl={6} md={6} className="hs-border-right-light h-100">
                <ListView
                  setSelectedContact={setSelectedContact}
                  services={services}
                  setShowDescription={setShowDescription}
                  numberList={numberList}
                  selectedContact={selectedContact}
                  handleNumberSelect={handleNumberSelect}
                  setIsChannelPresent={setIsChannelPresent}
                  filterContact={filterContact}
                  setFilterContact={setFilterContact}
                  setNumbersList={setNumbersList}
                  appProperties={appProperties}
                  selectedContactId={selectedContactId}
                  showConversations={showConversations}
                  setShowConversations={setShowConversations}
                  showDescription={showDescription}
                  newToNumberInputRef={newToNumberInputRef}
                  setShowChatOverlay={setShowChatOverlay}
                  setAppProperties={setAppProperties}
                  setImportContactsNotificationOpen={setImportContactsNotificationOpen}
                  setContactsAvailable={setContactsAvailable}
                  setSelectedService={setSelectedService}
                  selectedService={selectedService}
                  contactsAvailable={contactsAvailable}
                  showContactInfo={showContactInfo}
                  setShowContactInfo={setShowContactInfo}
                  onConversationsLoaded={onConversationsLoaded}
                  contactsLoaded={contactsLoaded}
                  setContactsLoaded={setContactsLoaded}
                  newFromNumber={newFromNumber}
                  setNewFromNumber={setNewFromNumber}
                  setConversationContext={setConversationContext}
                  setShowModalOverChat={setShowModalOverChat}
                  greaterThanLargeScreen={greaterThanLargeScreen}
                />
              </Col>
            )}
            <Col
              xs={showChatOverlay || chatView ? 24 : 0}
              xl={chatView ? 24 : showContactInfo && selectedContact ? 12 : 18}
              md={chatView ? 24 : showContactInfo && selectedContact ? 12 : 18}
              className={`${chatView ? "w-100 h-100" : "page-overlay"}`}
            >
              <DetailView
                showChatView={showChatView}
                setShowChatView={setShowChatView}
                showSwitch={showSwitch}
                setShowSwitch={setShowSwitch}
                toggleShowContactInfo={toggleShowContactInfo}
                newFromNumber={newFromNumber}
                setNewFromNumber={setNewFromNumber}
                selectedContactId={selectedContactId}
                chatView={chatView}
                channel={channel}
                selectedContact={selectedContact}
                isChannelPresent={isChannelPresent}
                services={services}
                handleNumberSelect={handleNumberSelect}
                toNumber={toNumber || selectedContact?.toNumber}
                setHelloSend={setHelloSend}
                associatedIntegID={selectedContact?.integId}
                setNumbersList={setNumbersList}
                numberList={numberList}
                omni={true}
                sendsms={sendsms}
                showConversations={chatView ? true : showConversations}
                setShowConversations={setShowConversations}
                showDescription={showDescription}
                phoneFieldConfiguration={phoneFieldConfiguration}
                setPhoneFieldConfiguration={setPhoneFieldConfiguration}
                setToNumberNumberInFormView={setToNumberNumberInFormView}
                newToNumberInputRef={newToNumberInputRef}
                setShowChatOverlay={setShowChatOverlay}
                subscribe={subscribe}
                unSubscribe={unSubscribe}
                rolesProperties={rolesProperties}
                setImportContactsNotificationOpen={setImportContactsNotificationOpen}
                contactsAvailable={contactsAvailable}
                showContactInfo={showContactInfo}
                onConversationsLoaded={onConversationsLoaded}
                setSelectedContact={setSelectedContact}
                setSelectedService={setSelectedService}
                selectedService={selectedService}
                setIsChannelPresent={setIsChannelPresent}
                handleOpenNotes={handleOpenNotes}
                handleFocusNote={handleFocusNote}
                noteIdToBeFocused={noteIdToBeFocused}
                setShowContactInfo={setShowContactInfo}
                contactsLoaded={contactsLoaded}
                showChatOverlay={showChatOverlay}
                showModalOverChat={showModalOverChat}
                setShowModalOverChat={setShowModalOverChat}
                greaterThanLargeScreen={greaterThanLargeScreen}
              />
            </Col>
            {selectedContact &&
              (showContactInfo && !greaterThanLargeScreen ? (
                <Drawer destroyOnClose={true} open={showContactInfo} className="w-100 h-100" onClose={() => setShowContactInfo(false)}>
                  <ContactInfoPanel
                    fromNumber={newFromNumber}
                    toNumber={toNumber || selectedContact?.toNumber}
                    contactId={selectedContactId.current}
                    leftServiceId={appProperties?.leftServiceId}
                    onClose={() => setShowContactInfo(false)}
                    showContactInfo={showContactInfo}
                    handleBlockNumberSuccess={() => {
                      setNumbersList((prev) => prev?.filter((item) => item?.orgCustomer !== selectedContact?.toNumber));
                      setShowContactInfo(false);
                      showDefaultInbox();
                    }}
                    showSwitch={showSwitch}
                    showChatView={showChatView}
                    setShowChatView={setShowChatView}
                    onNoteAdded={handleNoteUpdate}
                    onNoteUpdated={handleNoteUpdate}
                    onNoteDeleted={handleNoteUpdate}
                    noteIdToBeFocused={noteIdToBeFocused}
                    setNoteIdToBeFocused={setNoteIdToBeFocused}
                    sourceModule={conversationContext?.[selectedContactId.current]?.sourceModule}
                    sourceId={conversationContext?.[selectedContactId.current]?.sourceId}
                  />
                </Drawer>
              ) : (
                showContactInfo && (
                  <Col xl={6} md={6} xs={24} className="h-100">
                    <ContactInfoPanel
                      fromNumber={newFromNumber}
                      toNumber={toNumber || selectedContact?.toNumber}
                      contactId={selectedContactId.current}
                      leftServiceId={appProperties?.leftServiceId}
                      onClose={() => setShowContactInfo(false)}
                      showContactInfo={showContactInfo}
                      handleBlockNumberSuccess={() => {
                        setNumbersList((prev) => prev?.filter((item) => item?.orgCustomer !== selectedContact?.toNumber));
                        setShowContactInfo(false);
                        showDefaultInbox();
                      }}
                      showSwitch={showSwitch}
                      showChatView={showChatView}
                      setShowChatView={setShowChatView}
                      onNoteAdded={handleNoteUpdate}
                      onNoteUpdated={handleNoteUpdate}
                      onNoteDeleted={handleNoteUpdate}
                      noteIdToBeFocused={noteIdToBeFocused}
                      setNoteIdToBeFocused={setNoteIdToBeFocused}
                      sourceModule={conversationContext?.[selectedContactId.current]?.sourceModule}
                      sourceId={conversationContext?.[selectedContactId.current]?.sourceId}
                    />
                  </Col>
                )
              ))}
          </Row>
          {!chatView && <ImportContactsNotification open={importContactsNotificationOpen} />}
        </>
      )}
    </>
  );
}

export default ConversationView;

export const DetailView = React.memo(
  ({
    isChannelPresent,
    services,
    handleNumberSelect,
    toNumber,
    setNumbersList,
    numberList,
    omni,
    selectedContact,
    channel,
    associatedIntegID,
    chatView,
    setHelloSend,
    sendsms,
    selectedContactId,
    showConversations,
    setShowConversations,
    phoneFieldConfiguration,
    setPhoneFieldConfiguration,
    setToNumberNumberInFormView,
    newToNumberInputRef,
    setShowChatOverlay,
    subscribe,
    unSubscribe,
    rolesProperties,
    setImportContactsNotificationOpen,
    contactsAvailable,
    showContactInfo,
    setShowContactInfo,
    onConversationsLoaded,
    contactsLoaded,
    setSelectedService,
    setSelectedContact,
    setIsChannelPresent,
    newFromNumber,
    setNewFromNumber,
    toggleShowContactInfo,
    showSwitch,
    setShowSwitch,
    showChatView,
    setShowChatView,
    handleOpenNotes,
    handleFocusNote,
    showChatOverlay,
    showModalOverChat,
    setShowModalOverChat,
    greaterThanLargeScreen
  }) => {
    const { conversationContext, setConversationContext } = useContext(ConvContext);
    const [appProperties, setAppProperties] = useContext(AppContext);
    const { fetchData } = useHttp();
    const { fetchContactById } = useContacts(appProperties);
    const scrollableDivRef = useRef(null);
    const inboxDivPaginationRef = useRef(null);
    const isInboxPaginationDoneRef = useRef(null);
    const isFromFetchApi = useRef(null);
    const isOutboundDeliveredRef = useRef(0);
    const isAtBottomRef = useRef(true);
    const messageBodyRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [showConversationsChat, setShowConversationsChat] = useState(false);
    const [integId, setIntegId] = useState();
    const [savedNumbersList, setSavedNumbersList] = useState();
    const [fromNumberFlag, setFromNumberFlag] = useState(false);
    const [toNumberChatView, setToNumberChatView] = useState();
    const [fetchConversationLoading, setFetchConversationLoading] = useState(false);
    const [channelRedirectUrl, setChannelRedirectUrl] = useState(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const previousScrollHeight = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const balanceCredits =
      appProperties?.licenseObj?.licenseDetails?.creditsPurchased - appProperties?.licenseObj?.licenseDetails?.creditsConsumed;
    const { conversations = [], previewResponse } = conversationContext?.[selectedContactId.current] || {};

    // Calculate unread messages for backward compatibility - only count received messages (direction === 1)
    const unReadMessages = conversations?.filter((msg) => msg?.isRead === false && msg?.direction === MessageDirection.INBOUND) || [];

    const currentPhoneNumberObject = useMemo(
      () => appProperties?.savedNumbers?.find((number) => number?.phoneNumber === newFromNumber),
      [appProperties?.savedNumbers, newFromNumber]
    );

    const handleFromNumberChange = (e) => {
      setNewFromNumber(e);
    };

    const serviceObject = useMemo(() => {
      return findServiceInfoByServiceId(
        appProperties,
        appProperties?.installedApps?.find((installedApp) => installedApp?.integProps?.integId === currentPhoneNumberObject?.integId)?.right
          ?.service_id
      );
    }, [appProperties?.installedApps, currentPhoneNumberObject?.integId]);

    useEffect(() => {
      if (appProperties?.controller?.getUrlParamsForRedirection) {
        appProperties?.controller
          ?.getUrlParamsForRedirection(true, appProperties)
          .then(function (respUrl) {
            setChannelRedirectUrl(respUrl);
          })
          .catch(err => console.error(err));
      }
    }, [appProperties?.controller]);

    useEffect(() => {
      const fromNumber = newFromNumber;
      // resetting during contact object change is mandatory.. else previous scroll will be maintained
      previousScrollHeight.current = null;
      if (fromNumber) {
        // removing the new contact from the list if there is a contact found in the list with same from and to number
        setNumbersList((prevList) => {
          const oldContactIndex = prevList.findIndex((item) => {
            const toNumber = item.orgCustomer || item.toNumber;
            return item?.orgMember === fromNumber && selectedContact?.toNumber === toNumber;
          });
          // if the index is not -1 then remove the new contact from the list
          if (oldContactIndex !== -1) {
            prevList = prevList.filter((item) => {
              const toNumber = item.orgCustomer || item.toNumber;

              if (item?.new && toNumber === selectedContact?.toNumber) return false;
              return true;
            });
          }
          return prevList;
        });
      }
    }, [newFromNumber, selectedContact?.toNumber]);

    const getUrlForFetchConversations = (integ, fromFlag) => {
      let targetUrl;
      if (showChatView) {
        targetUrl =
          "channels/" +
          (integ || associatedIntegID) +
          "/conversationsList/" +
          encodeURIComponent(chatView ? toNumberChatView : toNumber) +
          "?channelName=" +
          encodeURIComponent(newFromNumber);
      } else {
        targetUrl =
          "channels/" +
          (integ || associatedIntegID) +
          "/conversation?orgMember=" +
          encodeURIComponent(newFromNumber) +
          "&orgCustomer=" +
          encodeURIComponent(chatView ? toNumberChatView : toNumber) +
          "&leftServiceId=" +
          appProperties?.leftServiceId;
      }
      if (inboxDivPaginationRef?.current?.lastKey) {
        if (selectedContactId?.current && selectedContactId?.current !== DEFAULT_CONTACT_ID) {
          targetUrl += "&contactId=" + selectedContactId.current;
        }
        targetUrl += "&lastKey=" + encodeURIComponent(inboxDivPaginationRef?.current?.lastKey ?? "");
      }
      return targetUrl;
    };

    useEffect(() => {
      if (toNumber && !chatView) {
        setToNumberChatView(toNumber);
        if (setToNumberNumberInFormView) {
          setToNumberNumberInFormView(toNumber);
        }
      } else if (toNumberChatView) {
        setToNumberChatView(toNumberChatView);

        if (setToNumberNumberInFormView) {
          setToNumberNumberInFormView(toNumberChatView);
        }
      }
    }, [toNumber, toNumberChatView]);

    // Track in-flight requests to prevent duplicates
    const activeRequestRef = useRef(null);

    async function fetchByList(integ, fromFlag, fromFetchApi, fromWhere) {
      // Skip if already loading, pagination is complete, or a request is in flight
      if (!fromFetchApi && (fetchConversationLoading || isInboxPaginationDoneRef?.current || !inboxDivPaginationRef?.current?.lastKey)) {
        return;
      }

      // Generate a unique URL for the current request
      const requestUrl = getUrlForFetchConversations(integ, fromFlag);

      // If the same URL is currently being fetched, don't make a duplicate request
      if (activeRequestRef.current === requestUrl) {
        return;
      }

      // Mark this URL as being fetched
      activeRequestRef.current = requestUrl;

      if (fromFetchApi) {
        isInboxPaginationDoneRef.current = null;
      }
      const unreadCount = conversationContext?.[selectedContactId.current]?.unreadCount || 0;
      let remainingUnreadToMark = unreadCount - unReadMessages?.length;

      setFetchConversationLoading(true);
      var currentContactId = selectedContactId.current;
      isFromFetchApi.current = fromFetchApi;
      await fetchData(getUrlForFetchConversations(integ, fromFlag), "GET", null, appProperties)
        .then(async function (response) {
          try {
            if (response) {
              response = JSON.parse(response);
              let conversations = response?.data?.data?.messages;
              inboxDivPaginationRef.current = response?.data?.data?.pagination;
              remainingUnreadToMark = response?.data?.data?.contact?.unreadCount - unReadMessages?.length; // updating 
              let reads = [];

              if (conversations?.length === 0 || !checkTruthy(response?.data?.data?.pagination)) {
                isInboxPaginationDoneRef.current = true;
              } else {
                isInboxPaginationDoneRef.current = null;
              }
              let sourceUserParticipant = response?.data?.data?.participants?.find((obj) => obj?.role === "source_user");
              setConversationContext((prev) => ({
                ...prev,
                [selectedContactId.current]: {
                  ...prev[selectedContactId.current],
                  originalContactInfo: sourceUserParticipant,
                },
              }));

              let endUser = response?.data?.data?.participants?.find((obj) => obj?.role === "end_user");
              if (sourceUserParticipant?.contactId && (chatView || currentContactId === DEFAULT_CONTACT_ID)) {
                if (!chatView && currentContactId === DEFAULT_CONTACT_ID) {
                  navigate({ pathname: `/inbox/${sourceUserParticipant.contactId}`, search: location.search }, { replace: true });
                }
                selectedContactId.current = currentContactId = sourceUserParticipant?.contactId;
                setNumbersList &&
                  setNumbersList((prevList) => {
                    const oldContactIndex = prevList?.findIndex((item) => {
                      return item?.new && item?.orgCustomer === endUser?.id;
                    });
                    if (oldContactIndex !== -1) {
                      prevList[oldContactIndex] = {
                        ...prevList[oldContactIndex],
                        contactId: sourceUserParticipant?.contactId,
                        contactOwnerEntityId: sourceUserParticipant?.contactOwnerEntityId,
                        conversationStatus: sourceUserParticipant?.conversationStatus,
                        conversationStatusType: sourceUserParticipant?.conversationStatusType,
                        orgMember: sourceUserParticipant?.id,
                        new: false,
                      };
                    }
                    return prevList;
                  });
              } else if (sourceUserParticipant?.contactId && currentContactId !== sourceUserParticipant?.contactId && !chatView) {
                selectedContactId.current = currentContactId = sourceUserParticipant?.contactId;
                var isContactAvailable = numberList?.some((obj) => obj?.contactId === sourceUserParticipant?.contactId);
                const savedNumberObject = savedNumbersList.find((obj) => obj?.phoneNumber === newFromNumber);
                if (!isContactAvailable) {
                  const contactData = await fetchContactById(sourceUserParticipant?.contactId);
                  if (contactData) {
                    isContactAvailable = true;
                    setNumbersList((prevList) => [contactData, ...(prevList || [])]);
                  }
                }
                if (!isContactAvailable) {
                  handleNumberSelect(toNumber, {
                    fromNumber: savedNumberObject?.phoneNumber,
                    toNumber: toNumber,
                    integId: savedNumberObject?.integId,
                    new: true,
                  });
                } else {
                  if (!chatView && currentContactId === DEFAULT_CONTACT_ID) {
                    navigate({ pathname: `/inbox/${sourceUserParticipant.contactId}`, search: location.search }, { replace: true });
                  }
                  selectedContactId.current = currentContactId = sourceUserParticipant?.contactId;
                }
              }
              // Get the unreadCount from the API response
              // Since messages are ordered by time (oldest first), create a copy to find the latest messages
              const conversationsReversed = Array.isArray(conversations) ? [...conversations].reverse() : [];

              // Track the indices of unread messages - only count received messages (direction === 1)
              const unreadMessageIndices = new Set();

              // First pass: mark the most recent received messages as unread
              for (let i = 0; i < conversationsReversed.length; i++) {
                const originalIndex = conversations.length - 1 - i;
                const msg = conversationsReversed[i];

                // Only mark received messages as unread (direction === 1)
                if (msg?.direction === MessageDirection.INBOUND && remainingUnreadToMark > 0) {
                  unreadMessageIndices.add(originalIndex); // Track the original index as unread
                  remainingUnreadToMark--;
                }

                if (remainingUnreadToMark === 0) break;
              }

              // Second pass: process all messages in original order
              conversations.forEach((conversationObj, index) => {
                // Only consider direction === 1 (received messages) for unread status
                const isUnread = unreadMessageIndices.has(index) && conversationObj?.direction === MessageDirection.INBOUND;

                const message = {
                  ...conversationObj,
                  isRead: !isUnread,
                };
                reads.push(message);
              });
              setConversationContext((prev) => ({
                ...prev,
                [sourceUserParticipant?.contactId || selectedContactId.current]: {
                  ...prev[sourceUserParticipant?.contactId || selectedContactId.current],
                  conversations: fromFetchApi ? reads : [...reads, ...(prev[selectedContactId.current]?.conversations || [])],
                  ...(sourceUserParticipant &&
                    clean({
                      conversationStatus: sourceUserParticipant.conversationStatus,
                      conversationStatusType: sourceUserParticipant.conversationStatusType,
                      contactOwnerEntityId: sourceUserParticipant.contactOwnerEntityId,
                      firstName: sourceUserParticipant.firstName,
                      lastName: sourceUserParticipant.lastName,
                      avatar: sourceUserParticipant.avatar,
                      integId: sourceUserParticipant.integId,
                      sourceModule: sourceUserParticipant.sourceModule,
                      sourceId: sourceUserParticipant.sourceId,
                      displayName: sourceUserParticipant.displayName || sourceUserParticipant.sourceFrom,
                    })),
                },
              }));

              const hasUnreadMessages = reads.some((msg) => msg?.isRead === false && msg?.direction === MessageDirection.INBOUND);
              if (hasUnreadMessages && fromFetchApi && !chatView) {
                const currentContact =
                  numberList?.find((item) => item.contactId === (sourceUserParticipant?.contactId || selectedContactId.current)) || selectedContact;

                const payload = createClearUnreadPayload(currentContact, sourceUserParticipant?.integId || integ);
                clearUnreadAPI(payload, reads);
              }

              setShowConversationsChat(true);
              setShowConversations(true);
              // Notify that conversations have been loaded
              if (fromFetchApi && onConversationsLoaded) {
                onConversationsLoaded();
              }
            }
          } catch (error) {
            console.error("Error processing response:", error);
            setConversationContext((prev) => ({
              ...prev,
              [selectedContactId.current]: { ...prev[selectedContactId.current], conversations: [] },
            }));
            setShowConversationsChat(true);
            setShowConversations(true);
          }
        })
        .catch((err) => {
        setShowConversationsChat(true);
        setShowConversations(true);
      })
        .finally(() => {
          setFetchConversationLoading(false);
          // Clear the active request reference when done
          activeRequestRef.current = null;
        });
    }

    useEffect(() => {
      const fetchApi = async () => {
        setShowConversationsChat(false);
        const currentContactInfo = conversationContext?.[selectedContactId.current] || {};

        let savedNumbers;
        if (!savedNumbersList) {
          var phoneObj = appProperties?.savedNumbers?.filter((obj) => obj?.isMessagingService !== "true");
          var msgObj = appProperties?.savedNumbers?.filter((obj) => obj?.isMessagingService === "true");
          if (msgObj?.length > 0) {
            phoneObj = [
              ...(phoneObj || []),
              {
                label: (
                  <Divider plain style={{ margin: 0, fontSize: "12px" }} orientation="left">
                    Message Service ID
                  </Divider>
                ),
                value: "divider",
                disabled: true,
              },
              ...msgObj,
            ];
          }
          setSavedNumbersList(phoneObj);
          savedNumbers = phoneObj;
          if (phoneObj?.length === 1 && !chatView && currentContactInfo?.new) {
            setNewFromNumber(phoneObj?.[0]?.phoneNumber);
          }
        }
        
        let list = savedNumbers || savedNumbersList;
        if (list?.length === 1 && currentContactInfo?.new) {
          setNewFromNumber(list?.[0]?.phoneNumber);
        }
        var integ;

        if (currentContactInfo?.displayName?.startsWith("Facebook") || currentContactInfo?.displayName?.startsWith("Instagram")) {
          integ = currentContactInfo?.integId;
          setIntegId(integ);
        }
        let fromFlag;
        if (!integ) {
          integ = list?.find((entry) => entry.phoneNumber === newFromNumber)?.integId;
          if (integ) {
            fromFlag = false;
            setFromNumberFlag(false);
          } else {
            fromFlag = true;
          }
        } else {
          setFromNumberFlag(true);
          fromFlag = true;
        }
        if (!newFromNumber || !integ) {
          setConversationContext((prev) => ({
            ...prev,
            [selectedContactId.current]: { ...prev[selectedContactId.current], conversations: [] },
          }));
        }
        setIntegId(integ);
        if ((toNumberChatView || toNumber) && newFromNumber) {
          setShowConversationsChat(false);
          setIntegId(integ);
          await fetchByList(integ, fromFlag, true);
        } else {
          setShowConversationsChat(true);
        }
      };
      if (appProperties && appProperties.apps) {
        fetchApi();
      }
      if (setHelloSend) {
        setHelloSend(savedNumbersList?.find((obj) => obj?.phoneNumber === newFromNumber)?.isHelloSendPhoneNumber === "true" ? true : false);
      }
      return () => {
        scrollableDivRef.current = null;
        inboxDivPaginationRef.current = null;
        isFromFetchApi.current = null;
      };
    }, [
      fetchData,
      chatView,
      associatedIntegID,
      showChatView,
      savedNumbersList,
      setHelloSend,
      appProperties.serviceInstalled,
      newFromNumber,
      toNumberChatView,
    ]);

    // Comprehensive scroll function that handles all scroll requirements:
    // 1. Scroll to first unread message on initial conversation load (stops at unread), or bottom if no unread messages
    // 2. Maintain position when loading previous conversations (pagination)
    // 3. Reset scroll position when selecting new contacts (scroll to first unread or bottom)
    const handleScrollFunction = useCallback(() => {
      const scrollableDiv = scrollableDivRef.current;

      if (!scrollableDiv) return;

      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        if (!scrollableDivRef.current) return;

        const div = scrollableDivRef.current;

        const stopShowingScrollButtonAndSetTrueForIsAtBottomRef = () => {
          setShowScrollButton(false);
          isAtBottomRef.current = true;
        };

        if (isFromFetchApi.current) {
          // Check if there are any unread received messages
          const hasUnreadMessages = conversations.some((msg) => msg?.isRead === false && msg?.direction === MessageDirection.INBOUND);
          const lastMessage = conversations[conversations.length - 1];
          const isInbound = lastMessage?.direction === MessageDirection.INBOUND && !isAtBottomRef.current;

          if (isInbound && hasUnreadMessages) {
            setShowScrollButton(true);
          } else if (hasUnreadMessages) {
            // Add a small delay to ensure DOM is rendered, then scroll to the first unread message
            setTimeout(() => {
              const unreadLabel = div.querySelector("#first-unread-message-label");

              if (unreadLabel) {
                // Scroll to the unread label with some offset from the top
                const labelPosition = unreadLabel.offsetTop;
                const containerHeight = div.clientHeight;
                const scrollPosition = Math.max(0, labelPosition - containerHeight * 0.15); // 15% from top for better visibility

                div.scrollTo({
                  top: scrollPosition,
                  behavior: "auto",
                });
              } else {
                // Fallback: scroll to bottom if we can't find the label
                div.scrollTop = div.scrollHeight;
              }
            }, 100);
          } else if (isOutboundDeliveredRef.current > 0) {
            isOutboundDeliveredRef.current = Math.max(0, isOutboundDeliveredRef.current - 1);
          } else {
            // No unread messages, scroll to bottom
            stopShowingScrollButtonAndSetTrueForIsAtBottomRef();
            div.scrollTop = div.scrollHeight;
          }
          // Fallback: Scroll to bottom for any other case
        } else if (previousScrollHeight?.current) {
          const newScrollHeight = div.scrollHeight;
          const scrollDelta = newScrollHeight - previousScrollHeight.current;

          if (scrollDelta > 0) {
            // Maintain relative position when new messages are added at top (pagination)
            div.style.scrollBehavior = "auto";
            div.scrollTop = scrollDelta + 200;
          }

          // Fallback: Scroll to bottom for any other case
          // Requirement 1 & 3: Scroll to first unread message for initial loads or new contact selection
        } else {
          stopShowingScrollButtonAndSetTrueForIsAtBottomRef();
          div.scrollTop = div.scrollHeight;
        }
      });
    }, [conversations]);

    useEffect(() => {
      handleScrollFunction();
    }, [conversations, handleScrollFunction]);

    useEffect(() => {
      const contactInfo = searchParams.get("contactInfo");

      try {
        if (contactInfo && appProperties?.serviceInstalled && contactsLoaded) {
          const contactInfoObj = JSON.parse(contactInfo);

          let queryObj = numberList ?? numberList?.find((obj) => obj?.orgCustomer === contactInfoObj?.phone);
          setNewFromNumber(null);
          handleNumberSelect(contactInfoObj?.lastName, {
            fromNumber: queryObj?.orgMember,
            toNumber: contactInfoObj?.phone,
            sourceModule: contactInfoObj?.module,
            sourceId: contactInfoObj?.id,
            avatar: contactInfoObj?.avatar,
            integId: queryObj?.integId,
            new: true,
          });
          searchParams.delete("contactInfo");
          setSearchParams(searchParams);
        }
      } catch (error) {
        console.error("Error processing response:", error);
      }
    }, [searchParams, appProperties?.serviceInstalled, contactsLoaded, numberList, handleNumberSelect, setSearchParams]);

    const handleSelectNewContact = (contact) => {
      // Contact object is now passed directly from the modal
      let queryObj = numberList ?? numberList?.find((obj) => obj?.orgCustomer === contact?.phone);
      setNewFromNumber(queryObj?.orgMember);
      const defaultAssigneeSettingValue = getSettingBySettingName(appProperties, "contactAssignment")?.settingValue || "crmLeadOwner";
      const contactOwnerEntityId = defaultAssigneeSettingValue === "crmLeadOwner"
        ? appProperties?.licenseObj?.allLicensedUserDetails?.find(
          (obj) => obj?.remoteUserId === contact?.recordOwnerId || obj?.email === contact?.recordOwnerEmail
        )?.osyncUserId
        : null;
      const recordOwnerAccountUserInfo = appProperties?.licenseObj?.allLicensedUserDetails?.find(
        (obj) => obj?.remoteUserId === contact[0]?.recordOwnerId || obj?.email === contact[0]?.recordOwnerEmail
      );
      // For new contacts without contactId, navigate to DEFAULT_CONTACT_ID
      const contactId = contact?.[0]?.contactId || contact?.[0]?.id || DEFAULT_CONTACT_ID;
      if (!chatView) {
        navigate({ pathname: `/inbox/${contactId}`, search: location.search }, { replace: true });
      }
      handleNumberSelect(
        contact?.lastName || contact?.id,
        {
          fromNumber: queryObj?.orgMember,
          toNumber: contact?.phone,
          sourceModule: contact?.module,
          sourceId: contact?.id,
          avatar: contact?.avatar,
          integId: queryObj?.integId,
          contactOwnerEntityId: contactOwnerEntityId,
          new: true,
        },
        contact
      );
    };
    const updateUnreadMessages = useCallback(() => {
      const unreadMessages = conversations?.filter((msg) => msg?.isRead === false && msg?.direction === MessageDirection.INBOUND) || [];
      if (unreadMessages?.length > 0) {
        isFromFetchApi.current = true;
        setConversationContext((prev) => ({
          ...prev,
          [selectedContactId.current]: {
            ...prev[selectedContactId.current],
            conversations: (prev[selectedContactId.current]?.conversations || []).map((message) => ({
              ...message,
              isRead: true,
            })),
          },
        }));
        setNumbersList((prev) => {
          const newNumbersList = prev.map((item) => {
            if (item?.orgMember === selectedContact?.fromNumber && item?.orgCustomer === selectedContact?.toNumber) {
              return {
                ...item,
                unreadCount: 0,
              };
            } else {
              return item;
            }
          });
          return newNumbersList;
        });
        setConversationContext((prev) => ({
          ...prev,
          [selectedContactId.current]: {
            ...(prev[selectedContactId.current] || {}),
            unreadCount: 0,
          },
        }));
      }
    }, [selectedContact?.fromNumber, selectedContact?.toNumber, selectedContactId, conversations, savedNumbersList]);

    // Helper to create clearUnread payload
    const createClearUnreadPayload = useCallback(
      (contactOverride = null, integOverride = null) => {
        const contact = contactOverride || selectedContact || numberList?.find((item) => item.contactId === selectedContactId.current);
        return {
          integId: integOverride || contact?.integId || integId,
          contactId: selectedContactId.current,
          orgMember: contact?.orgMember || contact?.fromNumber || newFromNumber,
          orgCustomer: contact?.orgCustomer || contact?.toNumber || toNumber || toNumberChatView,
        };
      },
      [selectedContact, numberList, selectedContactId, integId, newFromNumber, toNumber, toNumberChatView]
    );

    // Centralized function to clear unread messages on backend
    const clearUnreadAPI = useCallback(
      (payload = null, currentConversation) => {
        if(!currentConversation) {
          currentConversation = conversations
        }
        const unreadMessages = currentConversation?.filter((msg) => msg?.isRead === false && msg?.direction === MessageDirection.INBOUND) || [];

        if (unreadMessages?.length === 0) return;

        const clearUnreadPayload = payload || createClearUnreadPayload();

        // Validate and make API call
        const { integId: id, contactId, orgMember, orgCustomer } = clearUnreadPayload;
        if (id && contactId && orgMember && orgCustomer) {
          fetchData("clearUnread", "PUT", clearUnreadPayload, appProperties).catch((err) => {
            console.error("Failed to clear unread messages:", err);
          });
        }
      },
      [conversations, createClearUnreadPayload, fetchData, appProperties]
    );

    // Combined function to clear unread messages both locally and on backend
    const clearAllUnreadMessages = useCallback(
      (payload = null) => {     
        updateUnreadMessages();
        clearUnreadAPI(payload);
      },
      [updateUnreadMessages, clearUnreadAPI]
    );
    const handleSendSmsResp = (response, newUUID, idObject) => {
      var newContact;
      const contactId = response.data?.contact?.contactId || selectedContactId.current;
      const selectedContact = numberList?.find((item) => item.contactId === contactId);
      if ((chatView && (!conversations || conversations.length < 1)) || (!chatView && !selectedContact?.contactId)) {
        newContact = true;
      }
      if (response.data.status === true) {
        const updatedList = numberList?.map((item) => {
          if (
            item.contactId === response.data?.contact?.contactId ||
            (item.orgCustomer === idObject.id && item.orgMember === idObject.sender_id) ||
            (item.orgCustomer === idObject.id && item.new)
          ) {
            return { ...item, ...response.data?.contact, new: false };
          } else {
            return item;
          }
        });
        if (newContact) {
          selectedContactId.current = response?.data?.contact?.contactId;
        }
        if (contactId === selectedContactId?.current || newContact || chatView) {
          setConversationContext((prev) => ({
            ...prev,
            [contactId]: {
              ...(prev[contactId] || {}),
              displayName: response?.data?.contact?.sourceFrom,
            },
          }));
          isFromFetchApi.current = true;
          setConversationContext((prev) => {
            let existingIndex = prev[contactId]?.conversations?.findIndex((item) => item?.tempMessageId === newUUID);
            if (!existingIndex) {
              existingIndex = 0;
            }

            if (existingIndex !== -1) {
              const newList = newContact ? prev[DEFAULT_CONTACT_ID]?.conversations : prev[contactId]?.conversations;
              if (!newList) {
                return prev;
              }
              const messageData = {
                message: newList?.[existingIndex]?.message,
                components: newList?.[existingIndex]?.components,
                id: response.data?.messageId,
                reply_by: response.data?.reply_by,
                sender_url: response.data?.sender_url,
                createdTime: newList?.[existingIndex]?.createdTime,
                attachments: newList?.[existingIndex]?.attachments,
                direction: MessageDirection.OUTBOUND,
                messageStatus: "sent",
              };
              newList[existingIndex] = messageData;
              if (newContact) {
                const { [DEFAULT_CONTACT_ID]: _, ...rest } = prev;
                return {
                  ...rest,
                  [contactId]: {
                    ...prev[contactId],
                    conversations: newList,
                  },
                };
              }

              return {
                ...prev,
                [contactId]: {
                  ...prev[contactId],
                  conversations: newList,
                  contactOwnerEntityId: response?.data?.contact?.contactOwnerEntityId || prev[contactId]?.contactOwnerEntityId,
                },
              };
            } else {
              return {
                ...prev,
                [contactId]: {
                  ...prev[newContact ? DEFAULT_CONTACT_ID : contactId],
                  conversations: [...(prev[newContact ? DEFAULT_CONTACT_ID : contactId]?.conversations ?? []), response?.data],
                  contactOwnerEntityId: response?.data?.contact?.contactOwnerEntityId || prev[newContact ? DEFAULT_CONTACT_ID : contactId]?.contactOwnerEntityId,
                },
              };
            }
          });
        }
        setNumbersList(updatedList);
      } else {
        if (response?.data) {
          if (response?.data?.license?.code) {
            response.data.data = response?.data?.license?.description;
            response.data.errorCode = response?.data?.license?.code;
          }
          if (newContact) {
            selectedContactId.current = contactId;
          }
          isFromFetchApi.current = true;
          setConversationContext((prev) => ({
            ...prev,
            [contactId]: {
              ...prev[newContact ? DEFAULT_CONTACT_ID : contactId],
              conversations: (prev[newContact ? DEFAULT_CONTACT_ID : contactId]?.conversations || []).map((conversation) => {
                if (conversation.tempMessageId === newUUID) {
                  return {
                    ...conversation,
                    direction: MessageDirection.OUTBOUND,
                    errorMessage: response?.data?.data || response?.data?.errorMessage,
                    errorCode: response?.data?.errorCode,
                    messageStatus: "failed",
                  };
                }
                return conversation;
              }),
              contactOwnerEntityId: response?.data?.contact?.contactOwnerEntityId || prev[newContact ? DEFAULT_CONTACT_ID : contactId]?.contactOwnerEntityId,
            },
          }));
          setNumbersList((prev) =>
            (prev || []).map((item) => {
              if (
                item?.contactId === response.data?.contact?.contactId ||
                (item?.orgCustomer === idObject.id && item?.orgMember === idObject.sender_id) ||
                (item?.orgCustomer === idObject.id && item?.new)
              ) {
                return response.data?.contact || item;
              } else {
                return item;
              }
            })
          );
        }
      }
      if (response.data?.omniLicense) {
        setAppProperties((prev) => ({
          ...prev,
          licenseObj: {
            ...prev.licenseObj,
            licenseDetails: response.data?.omniLicense,
          },
        }));
      }
    };

    const handlePreviewResponse = (response) => {
      setConversationContext((prev) => ({
        ...prev,
        [selectedContactId.current]: { ...prev[selectedContactId.current], previewResponse: response },
      }));
    };

    const currentFileList = conversationContext?.[selectedContactId.current]?.fileList || [];
    const handleMediaFileList = (fileList) => {
      const contactIdOfUploadedMediaFile = fileList?.[0]?.contactId ?? selectedContactId.current;
      setConversationContext((prev) => ({
        ...prev,
        [contactIdOfUploadedMediaFile]: {
          ...prev[contactIdOfUploadedMediaFile],
          fileList: fileList,
        },
      }));
    };
    const { uploadAttachment } = useAttachment(appProperties, currentFileList, handleMediaFileList, false);

    const [isDraggingOverAndUploadAllowed, setIsDraggingOverAndUploadAllowed] = useState(false);

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.types.includes("Files") && selectedContactId.current !== DEFAULT_CONTACT_ID) {
        setIsDraggingOverAndUploadAllowed(true);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOverAndUploadAllowed(false);
    };

    const handleDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOverAndUploadAllowed(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;

      if (!integId) {
        HNotification.notify({ type: "error", message: "MMS Alert", description: "From number is required to upload files" });
        return;
      }

      for (const file of files) {
        await uploadAttachment(file, null, null, null, null, integId, null, selectedContactId.current);
      }
    };

    return (
      <Flex
        vertical={true}
        justify="space-between"
        className={`bg-white h-100 ${isDraggingOverAndUploadAllowed ? "hs-file-upload-drag-over-bg" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <DropOverlay isDraggingOver={isDraggingOverAndUploadAllowed} />
        {showConversations ? (
          <>
            {isChannelPresent || !omni || chatView ? (
              <>
                {chatView ? (
                  <ChatViewHeader
                    appProperties={appProperties}
                    newFromNumber={newFromNumber}
                    setNewFromNumber={setNewFromNumber}
                    savedNumbersList={savedNumbersList}
                    setSavedNumbersList={setSavedNumbersList}
                    setToNumberChatView={setToNumberChatView}
                    phoneFieldConfiguration={phoneFieldConfiguration}
                    setPhoneFieldConfiguration={setPhoneFieldConfiguration}
                    selectedContactId={selectedContactId}
                  />
                ) : (
                  <ConversationHeader
                    showSwitch={showSwitch}
                    setShowSwitch={setShowSwitch}
                    handleFromNumberChange={handleFromNumberChange}
                    setSavedNumbersList={setSavedNumbersList}
                    savedNumbersList={savedNumbersList}
                    toNumber={toNumber}
                    showChatView={showChatView}
                    setShowChatView={setShowChatView}
                    newFromNumber={newFromNumber}
                    setShowChatOverlay={setShowChatOverlay}
                    appProperties={appProperties}
                    showContactInfo={showContactInfo}
                    toggleShowContactInfo={toggleShowContactInfo}
                    setSelectedService={setSelectedService}
                    setSelectedContact={setSelectedContact}
                    selectedContactId={selectedContactId}
                    setIsChannelPresent={setIsChannelPresent}
                    services={services}
                    chatView={chatView}
                    conversations={conversations}
                    integId={integId}
                    setShowContactInfo={setShowContactInfo}
                    setNumbersList={setNumbersList}
                    greaterThanLargeScreen={greaterThanLargeScreen}
                  />
                )}
                {showConversationsChat ? (
                  <>
                    {conversations?.length > 0 ? (
                      <ConversationMessages
                        setConversationContext={setConversationContext}
                        sendsms={sendsms}
                        updateUnreadMessages={updateUnreadMessages}
                        clearAllUnreadMessages={clearAllUnreadMessages}
                        conversations={conversations}
                        unReadMessages={
                          conversations?.filter((msg) => msg?.isRead === false && msg?.direction === MessageDirection.INBOUND) || []
                        }
                        contactInfo={
                          conversationContext?.[selectedContactId.current]?.originalContactInfo ||
                          conversationContext?.[selectedContactId.current]
                        }
                        scrollableDivRef={scrollableDivRef}
                        integId={integId}
                        appProperties={appProperties}
                        handleSendSmsResp={handleSendSmsResp}
                        setNumbersList={setNumbersList}
                        toNumberChatView={toNumberChatView}
                        toNumber={toNumber}
                        balanceCredits={balanceCredits}
                        chatView={chatView}
                        fetchByList={fetchByList}
                        previousScrollHeight={previousScrollHeight}
                        fetchConversationLoading={fetchConversationLoading}
                        selectedContactId={selectedContactId}
                        channelRedirectUrl={channelRedirectUrl}
                        previewResponse={previewResponse}
                        handleFocusNote={handleFocusNote}
                        showScrollButton={showScrollButton}
                        setShowScrollButton={setShowScrollButton}
                        isAtBottomRef={isAtBottomRef}
                        isFromFetchApi={isFromFetchApi}
                      />
                    ) : (
                      <NoConversations
                        unipilePhoneNumberObj={
                          currentPhoneNumberObject?.phoneNumber?.startsWith("unipile_whatsapp:::") ||
                            currentPhoneNumberObject?.phoneNumber?.startsWith("hsqr_whatsapp:::")
                            ? currentPhoneNumberObject
                            : null
                        }
                        channelRedirectUrl={channelRedirectUrl}
                        navigate={navigate}
                        chatView={chatView}
                        appProperties={appProperties}
                        isContactAvailable={contactsAvailable}
                        newToNumberInputRef={newToNumberInputRef}
                        setNumbersList={setNumbersList}
                        setImportContactsNotificationOpen={setImportContactsNotificationOpen}
                      />
                    )}
                    {currentPhoneNumberObject?.showDailyLimitReachedWarning && serviceObject?.displayName ? (
                      <div className="w-100 d-flex justify-content-center">
                        <div className="hs-w-95">
                          <MessageAreaInfo
                            id={`${toNumber}:${newFromNumber}`}
                            type="warning"
                            message={getHighUsageAlertMessage(serviceObject?.displayName)}
                          />
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  <div className="d-flex flex-column align-items-center justify-content-center m-auto">
                    <LoadingOutlined
                      spin
                      style={{
                        fontSize: 24,
                        color: "#605BFF",
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="d-flex flex-grow-* flex-column align-items-center justify-content-center h-100">
                  <NoConversations
                    unipilePhoneNumberObj={
                      currentPhoneNumberObject?.phoneNumber?.startsWith("unipile_whatsapp:::") ||
                        currentPhoneNumberObject?.phoneNumber?.startsWith("hsqr_whatsapp:::")
                        ? currentPhoneNumberObject
                        : null
                    }
                    setImportContactsNotificationOpen={setImportContactsNotificationOpen}
                    setNumbersList={setNumbersList}
                    navigate={navigate}
                    channelRedirectUrl={channelRedirectUrl}
                    chatView={chatView}
                    appProperties={appProperties}
                    isContactAvailable={contactsAvailable}
                    newToNumberInputRef={newToNumberInputRef}
                  />
                </div>
              </>
            )}
            {((services?.length > 0 || numberList?.length > 0) && showConversations && toNumber) || chatView || !omni ? (
              <div>
                {(conversations || unReadMessages) &&
                  !(conversations?.length > 0) &&
                  newFromNumber?.split(":::")[0] === "whatsapp" &&
                  !fetchConversationLoading && (
                    <div className="w-100 d-flex justify-content-center mb-2">
                      <div className="hs-w-95">
                        <MessageAreaInfo
                          id={`${toNumber}:${newFromNumber}`}
                          icon={true}
                          message={`The first message must be a pre-approved ${WHATSAPP_DISPLAY_NAME} template.`}
                        />
                      </div>
                    </div>
                  )}
                <MessageArea
                  selectedContact={selectedContact}
                  handleMediaFileList={handleMediaFileList}
                  handleFromNumberChange={handleFromNumberChange}
                  setConversationContext={setConversationContext}
                  conversationContext={conversationContext}
                  handleSendSmsResp={handleSendSmsResp}
                  appProperties={appProperties}
                  unReadMessages={conversations?.filter((msg) => msg?.isRead === false && msg?.direction === MessageDirection.INBOUND) || []}
                  updateUnreadMessages={updateUnreadMessages}
                  clearAllUnreadMessages={clearAllUnreadMessages}
                  clearUnreadAPI={clearUnreadAPI}
                  toNumberChatView={toNumberChatView}
                  integId={integId}
                  messageBodyRef={messageBodyRef}
                  conversations={conversations}
                  numberList={numberList}
                  toNumber={toNumber}
                  setNumbersList={setNumbersList}
                  newFromNumber={newFromNumber}
                  setNewFromNumber={setNewFromNumber}
                  chatView={chatView}
                  savedNumbersList={savedNumbersList}
                  setSavedNumbersList={setSavedNumbersList}
                  sendsms={sendsms}
                  channel={channel}
                  contactId={selectedContactId}
                  balanceCredits={balanceCredits}
                  isFromFetchApi={isFromFetchApi}
                  previewResponse={previewResponse}
                  setPreviewResponse={handlePreviewResponse}
                  handlePreviewResponse={handlePreviewResponse}
                  fetchConversationLoading={fetchConversationLoading}
                  subscribe={subscribe}
                  unSubscribe={unSubscribe}
                  setAppProperties={setAppProperties}
                  rolesProperties={rolesProperties}
                  handleOpenNotes={handleOpenNotes}
                  isOutboundDeliveredRef={isOutboundDeliveredRef}
                  greaterThanLargeScreen={greaterThanLargeScreen}
                />
              </div>
            ) : null}
          </>
        ) : null}
        <NewConversationModal
          isOpen={showModalOverChat}
          onClose={() => {
            setShowModalOverChat(false);
            setShowChatOverlay(false);
          }}
          appProperties={appProperties}
          chatView={chatView}
          handleSelectNewContact={handleSelectNewContact}
          handleNumberSelect={handleNumberSelect}
          setNewFromNumber={setNewFromNumber}
          messageBodyRef={messageBodyRef}
          setShowChatOverlay={setShowChatOverlay}
        />
      </Flex>
    );
  }
);

const MessageArea = ({
  selectedContact,
  integId,
  messageBodyRef,
  appProperties,
  chatView,
  conversations,
  setNumbersList,
  numberList,
  savedNumbersList,
  setSavedNumbersList,
  toNumber,
  sendsms,
  newFromNumber,
  updateUnreadMessages,
  clearAllUnreadMessages,
  clearUnreadAPI,
  toNumberChatView,
  setNewFromNumber,
  unReadMessages,
  channel,
  contactId,
  handleSendSmsResp,
  balanceCredits,
  isFromFetchApi,
  previewResponse,
  setPreviewResponse,
  fetchConversationLoading,
  conversationContext,
  setConversationContext,
  handlePreviewResponse,
  subscribe,
  setAppProperties,
  rolesProperties,
  handleFromNumberChange,
  handleOpenNotes,
  handleMediaFileList,
  isOutboundDeliveredRef,
  greaterThanLargeScreen
}) => {
  const [templateIdRef, setTemplateIdRef] = useState(null);
  const [sendBtnLoading, setSendBtnLoading] = useState(false);
  const [messageAreaLoading, setMessageAreaLoading] = useState(false);
  const { fetchStripeURL } = useLicense();
  const [resource, setResource] = useState("");
  const [templateOpen, setTemplateOpen] = useState(false);
  const [messageHeaderTemplateOpen, setMessageHeaderTemplateOpen] = useState(false);
  const messageTextAreaContentRef = useRef(null);
  const antIcon = <LoadingOutlined className="loading-button" spin />;
  const { templateId = null, fileList = [], isAiGenerated = false } = conversationContext?.[contactId.current] || {};
  // const [draftMessages, setDraftMessages] = useState([]);
  const draftMessages = useRef({});
  const [currentTextAreaValue, setCurrentTextAreaValue] = useState(draftMessages.current[contactId.current] || "");
  const cursorPositionRef = useRef(currentTextAreaValue?.length || 0);

  function updateConversationContext(updates) {
    setConversationContext((prev) => ({
      ...prev,
      [contactId.current]: {
        ...prev?.[contactId.current],
        ...updates,
      },
    }));
  }

  const handleMessage = useCallback(
    (messageText, onTextAreaInputEvent, messageSource = "messageArea") => {
      if (!templateOpen && messageText?.trim?.() === "/") {
        setTemplateOpen(true);
      } else if (templateOpen) {
        setTemplateOpen(false);
      }
      setCurrentTextAreaValue((prev) => {
        let currentMessage = prev ?? "";
        let updatedMessage = "";
        if (messageText?.native) {
          updatedMessage =
            currentMessage.slice(0, cursorPositionRef.current) + messageText.native + currentMessage.slice(cursorPositionRef.current);
          cursorPositionRef.current = cursorPositionRef.current + messageText.native.length;
        } else {
          updatedMessage = messageText;
          if (onTextAreaInputEvent) {
            cursorPositionRef.current = onTextAreaInputEvent?.target?.selectionStart || messageText?.length;
          }
        }

        return updatedMessage;
      });

      const isAiGeneratedMessage = messageSource === "ai-reply";
      if (messageText === "" || isAiGeneratedMessage || messageSource === "templates") {
        setConversationContext((prev) => {
          return {
            ...prev,
            [contactId.current]: {
              ...prev?.[contactId.current],
              isAiGenerated: isAiGeneratedMessage,
            },
          };
        });
      }
    },
    [templateOpen, contactId]
  );

  useEffect(() => {
    updateConversationContext({ templateId: templateIdRef });
  }, [contactId, templateIdRef]);

  function handleMedia(mediaFile) {
    const fileList = mediaFile ? mapToMediaList(mediaFile, false) : [];
    updateConversationContext({ fileList });
  }

  useEffect(() => {
    appProperties?.controller?.getAssociatedObjectType(appProperties).then(function (currentModuleFromApplication) {
      if (currentModuleFromApplication) {
        setResource(currentModuleFromApplication);
      }
    });
  }, [appProperties?.controller, appProperties?.controller?.serviceInitialized]);

  function handleCursorPosition(event) {
    cursorPositionRef.current = event.target.selectionStart;
  }

  useEffect(() => {
    subscribe(
      "CONVERSATION_UPDATE",
      (data) => {
        if (data) {
          try {
            let response = JSON.parse(data?.data);
            if (
              response?.contactId === contactId.current ||
              ([newFromNumber, toNumberChatView, toNumberChatView?.replace(/[\s()-]/g, "")].includes(response?.sender_id) &&
                [newFromNumber, toNumberChatView, toNumberChatView?.replace(/[\s()-]/g, "")].includes(response?.receiver_id))
            ) {
              if (response?.messageStatus === "failed") {
                response.class = "input_error";
              } else {
                response.isRead = false;
              }
              response.status = response?.messageStatus;
              response.id = response?.messageId;
              const payload = {
                integId: response?.integId,
                contactId: contactId.current,
                orgMember: newFromNumber,
                orgCustomer: chatView ? toNumberChatView : toNumber,
              };
              if (response?.direction === MessageDirection.INBOUND) {
                // Use centralized clearUnread function
                clearUnreadAPI(payload);

                // playNotificationSound();
              } else if (response?.direction === MessageDirection.OUTBOUND && response?.messageStatus === "delivered") {
                isOutboundDeliveredRef.current = isOutboundDeliveredRef.current + 1;
              }
              const unReadMessages = conversations?.filter((msg) => msg?.isRead === false && msg?.direction === MessageDirection.INBOUND) || [];
              if (unReadMessages?.length > 0) {
                updateUnreadMessages();
              }
              isFromFetchApi.current = true;
              setConversationContext((prev) => {
                const existingIndex = prev?.[contactId?.current]?.conversations?.findIndex(
                  (item) => item?.id === response?.messageId || item?.id === response?.internalMessageId,
                );
                if (existingIndex >= 0) {
                  const newList = [...(prev[contactId?.current]?.conversations ?? [])];
                  newList[existingIndex] = {
                    ...newList[existingIndex],
                    ...response,
                  };
                  prev[contactId?.current].conversations = newList;
                  return prev;
                } else {
                  prev[contactId?.current].conversations = [...(prev[contactId?.current]?.conversations || []), response];
                  prev[contactId?.current].conversationStatusType = appProperties?.enums?.conversationStatusType?.OPEN?.code;
                  prev[contactId?.current].conversationStatus = appProperties?.enums?.conversationStatus?.OPEN?.code;
                  return prev;
                }
              });
            }
            setNumbersList((prev) => {
              const existingIndex = prev?.findIndex((item) => item?.contactId === response?.contactId);
              if (existingIndex !== -1) {
                const newList = [...prev];
                newList[existingIndex] = {
                  ...newList[existingIndex],
                  messageTagStatus: response?.messageStatus,
                };
                return newList;
              } else {
                return [...(prev || [])];
              }
            });
          } catch (e) {
            console.error("ERROR::::", e);
          }
        }
      },
      false,
      true,
    );
  }, [channel, toNumberChatView, newFromNumber]);

  useEffect(() => {
    setCurrentTextAreaValue(draftMessages.current[contactId.current] || "");
    return () => {
      setCurrentTextAreaValue("");
    };
  }, [contactId.current]);

  const handleSendSMS = async () => {
    const trimedCurrentTextAreaValue = currentTextAreaValue?.trim();
    if (fetchConversationLoading) {
      return;
    }
    clearAllUnreadMessages();
    let newUUID = crypto.randomUUID();
    if (!newFromNumber) {
      HNotification.notify({
        type: "error",
        message: "Error",
        description: "From Number is required !",
      });
    } else if (!toNumber && !toNumberChatView) {
      HNotification.notify({
        type: "error",
        message: "Error",
        description: "To Number is required !",
      });
    } else if (trimedCurrentTextAreaValue === "" && fileList?.length === 0 && !previewResponse?.components) {
      HNotification.notify({
        type: "error",
        message: "Error",
        description: "Message Body is required !",
      });
    } else if (fileList?.some((file) => file?.status === "uploading")) {
      HNotification.notify({
        type: "error",
        message: "Error",
        description: "Please wait for the file to upload",
      });
    } else {
      setSendBtnLoading(true);
      let associatedObjectId = null;
      let associatedObjectType = null;

      if (chatView) {
        associatedObjectId = await appProperties?.controller?.getAssociatedObjectId(appProperties);
        associatedObjectType = await appProperties?.controller?.getAssociatedObjectType(appProperties);
      } else if (contactId.current && contactId.current !== DEFAULT_CONTACT_ID && numberList?.length > 0) {
        let currentContact = numberList.find((obj) => obj?.contactId === contactId.current);
        associatedObjectId = currentContact?.sourceId;
        associatedObjectType = currentContact?.sourceModule;
      } else if (selectedContact) {
        associatedObjectId = selectedContact?.sourceId;
        associatedObjectType = selectedContact?.sourceModule;
      }
      const mediaFileList =
        fileList?.length > 0
          ? fileList?.map((file) => {
            return {
              name: file.name,
              url: file.url,
              contentType: file.type,
              size: file.size,
            };
          })
          : [];
      var body = {
        from: newFromNumber,
        pageId: newFromNumber,
        to: chatView ? toNumberChatView : toNumber,
        messageBody: trimedCurrentTextAreaValue,
        mediaFileList: mediaFileList,
        userId: urlParams.get("userId"),
        associatedObjectId: associatedObjectId,
        associatedObjectType: associatedObjectType,
        templateId: templateId,
        components: previewResponse?.components,
        fieldNameAndValueMap: previewResponse?.fieldNameAndValueMap,
      };
      let rightPhoneObj = appProperties.installedApps?.find((obj) => obj?.integProps?.integId === integId);

      if (rightPhoneObj?.right?.auth?.ignoreAuthorizedCheck || rightPhoneObj?.right?.auth?.authorized) {
        isFromFetchApi.current = true;
        setCurrentTextAreaValue("");
        updateConversationContext({ isAiGenerated: false });
        setTemplateIdRef(null);
        setPreviewResponse({});
        var idObject = {
          sender_id: newFromNumber,
          id: toNumber,
          class: "input-error",
          tempMessageId: newUUID,
          messageStatus:
            !CREDIT_BASED_SERVICES.includes(rightPhoneObj?.right?.service_name) ||
              (CREDIT_BASED_SERVICES.includes(rightPhoneObj?.right?.service_name) && balanceCredits > 0.5)
              ? "queued"
              : "failed",
          errorMessage:
            CREDIT_BASED_SERVICES.includes(rightPhoneObj?.right?.service_name) && balanceCredits < 0.5 ? (
              <span>
                Message sending failed due to low credits. Please
                <BuyFunds />.
              </span>
            ) : null,
          direction: MessageDirection.OUTBOUND,
          components: previewResponse?.components,
          message: trimedCurrentTextAreaValue,
          attachments: fileList?.length > 0 ? fileList : null,
          createdTime: new Date().toISOString(),
        };

        setNumbersList((prev) =>
          (prev || []).map((item) => {
            if ((item.orgCustomer === idObject.id && item.orgMember === idObject.sender_id) || (item.orgCustomer === idObject.id && item.new)) {
              return {
                ...item,
                orgMember: newFromNumber,
                sourceFrom: rightPhoneObj?.right?.service_display_name,
                message: trimedCurrentTextAreaValue,
                messageTag: trimedCurrentTextAreaValue,
                messageTagStatus:
                  !CREDIT_BASED_SERVICES.includes(rightPhoneObj?.right?.service_name) ||
                    CREDIT_BASED_SERVICES.includes(rightPhoneObj?.right?.service_name) ||
                    balanceCredits > 0.5
                    ? "queued"
                    : "failed",

                messageTagType: fileList?.[fileList?.length - 1]?.type,
                messageTime: dayjs().format("YYYY-MM-DD HH:mm"),
              };
            } else {
              return item;
            }
          }),
        );
        setConversationContext((prev) => ({
          ...prev,
          [contactId.current]: {
            ...prev?.[contactId.current],
            conversations: [...(prev?.[contactId?.current]?.conversations || []), idObject],
            fileList: [],
          },
        }));
        if (
          !CREDIT_BASED_SERVICES.includes(rightPhoneObj?.right?.service_name) ||
          (CREDIT_BASED_SERVICES.includes(rightPhoneObj?.right?.service_name) && balanceCredits > 0.5)
        ) {
          sendsms(integId, body)
            .then(function (response) {
              try {
                if (response) {
                  response = JSON.parse(response);
                  handleSendSmsResp(response, newUUID, idObject);
                  setSendBtnLoading(false);
                }
              } catch (error) {
                console.error("Error processing response:", error);
              }
            })
            .catch((error) => {
            setSendBtnLoading(false);
            setConversationContext((prev) => {
              var existingIndex = prev[contactId?.current]?.conversations?.findIndex((item) => item?.tempMessageId === newUUID);
              if (existingIndex !== -1) {
                const newList = [...prev[contactId?.current]?.conversations];
                let messageData = {
                  message: trimedCurrentTextAreaValue,
                  createdTime: newList[existingIndex]?.createdTime,
                  attachments: newList[existingIndex]?.attachments,
                  direction: MessageDirection.OUTBOUND,
                  class: "input-error",
                  messageStatus: "failed",
                };
                newList[existingIndex] = messageData;
                prev[contactId?.current].conversations = newList;
              }
              return prev;
            });
          });
        }
      } else {
        if (rightPhoneObj?.right) {
          openServiceNotAuthorizedModal(rightPhoneObj?.right, appProperties);
        }
        setSendBtnLoading(false);
      }
    }
  };

  useEffect(() => {
    if (sendBtnLoading) {
      setTemplateIdRef();
    }
  }, [sendBtnLoading]);

  useEffect(() => {
    const currentContactInfo = conversationContext?.[contactId.current] || {};
    let serviceObject = Object.values(appProperties?.apps || {})
      .flat()
      .find((service) => service?.displayName === currentContactInfo?.displayName);
    let rightServiceName = serviceObject?.name;
    if (currentContactInfo?.integId) {
      rightServiceName = appProperties?.installedApps?.find((obj) => obj?.integProps?.integId === currentContactInfo?.integId)?.right
        ?.service_name;
    } else {
      rightServiceName = savedNumbersList?.find((obj) => obj?.phoneNumber === newFromNumber)?.serviceName;
    }
    if (rightServiceName) {
      let assignerObj1 = Object.assign({}, ProviderCommonObj, ProviderExtendedCommonObj[rightServiceName]);
      appProperties.providerCommonObj = assignerObj1;
    }
  }, [conversationContext, contactId, newFromNumber, savedNumbersList, appProperties?.apps, appProperties?.installedApps]);

  // Reset providerCommonObj to default when leaving inbox
  useEffect(() => {
    return () => {
      if (setAppProperties) {
        setAppProperties((prev) => ({
          ...prev,
          providerCommonObj: Object.assign({}, ProviderCommonObj),
        }));
      }
    };
  }, []);

  useEffect(() => {
    if (templateId) messageBodyRef?.current?.focus();
  }, [templateId]);

  const handlePreviewRemove = () => {
    setPreviewResponse((prev) => {
      return { ...prev, components: null };
    });
    setTemplateIdRef(null);
  };

  const messageTextAreaRowCount = 3;

  const handleManageSubscription = (e) => {
    if (appProperties.licenseHandledBy === "STRIPE") {
      fetchStripeURL(appProperties).then(function (stripeLink) {
        window.open(stripeLink.url, "_blank");
      });
    } else {
      appProperties.controller.pricingLink(appProperties).then(function (resp) {
        window.open(resp, "_blank");
      });
    }
  };

  let prompt;
  const conversation = conversationContext?.[contactId?.current]?.conversations || [];
  if (Array.isArray(conversation) && conversation.length > 0) {
    let last10Messages = conversation.slice(-10)?.filter((msg) => msg?.direction !== MessageDirection.NONE);
    let formattedMessages = last10Messages
      .map((msg) => {
        let direction = msg?.direction === MessageDirection.INBOUND ? "Customer" : "Agent";
        return `${direction}: ${msg?.message}`;
      })
      .join("\n");

    if (currentTextAreaValue?.length > 1) {
      prompt = promptForRephraseMessageBasedOnConversation(formattedMessages, currentTextAreaValue);
    } else {
      prompt = promptForSuggestReply(formattedMessages);
    }
  } else {
    if (currentTextAreaValue?.length > 10) {
      prompt = promptForRephraseMessage(currentTextAreaValue);
    } else {
      prompt = promptForNormalGreetingMessage();
    }
  }

  useEffect(() => {
    draftMessages.current[contactId.current] = currentTextAreaValue;
  }, [currentTextAreaValue]);

  return (
    <div className="mb-1">
      <CommonMessageTextArea
        setNumbersList={setNumbersList}
        message={currentTextAreaValue}
        messageTextAreaContentRef={messageTextAreaContentRef}
        fileList={fileList}
        handleMediaFileList={handleMediaFileList}
        appProperties={appProperties}
        handleManageSubscription={handleManageSubscription}
        rolesProperties={rolesProperties}
        conversationContext={conversationContext}
        chatView={chatView}
        toNumberChatView={toNumberChatView}
        toNumber={toNumber}
        setMessageAreaLoading={setMessageAreaLoading}
        setTemplateId={setTemplateIdRef}
        templateId={templateId}
        handleMessage={handleMessage}
        handleMedia={handleMedia}
        setPreviewResponse={setPreviewResponse}
        integId={integId}
        moduleName={chatView ? resource : ""}
        previewResponse={previewResponse}
        isAiGenerated={isAiGenerated}
        messageAreaLoading={messageAreaLoading}
        contactId={contactId}
        messageTextAreaRowCount={messageTextAreaRowCount}
        unReadMessages={unReadMessages}
        clearAllUnreadMessages={clearAllUnreadMessages}
        source={"conversation"}
        handleSendSMS={handleSendSMS}
        handleCursorPosition={handleCursorPosition}
        messageBodyRef={messageBodyRef}
        prompt={prompt}
        setMessage={setCurrentTextAreaValue}
        setConversationContext={setConversationContext}
        setShortcutTemplateOpen={setTemplateOpen}
        shortcutTemplateOpen={templateOpen}
        iconTemplateOpen={messageHeaderTemplateOpen}
        setIconTemplateOpen={setMessageHeaderTemplateOpen}
        handlePreviewResponse={handlePreviewResponse}
        handleOpenNotes={handleOpenNotes}
        conversations={conversations}
        handleFromNumberChange={handleFromNumberChange}
        savedNumbersList={savedNumbersList}
        setSavedNumbersList={setSavedNumbersList}
        antIcon={antIcon}
        sendBtnLoading={sendBtnLoading}
        newFromNumber={newFromNumber}
        moduleNameForUserAccessCheck={"Inbox"}
        handlePreviewRemove={handlePreviewRemove}
        previewParams={{
          associatedId: conversationContext?.[contactId.current]?.sourceId || null,
          associatedModule: conversationContext?.[contactId.current]?.sourceModule || null,
        }}
        messageAreaDataCy="message-textarea-conversation-page"
      />
    </div>
  );
};

function SendMessageButtonContainer({
  conversations,
  handleFromNumberChange,
  savedNumbersList,
  setSavedNumbersList,
  integId,
  chatView,
  antIcon,
  sendBtnLoading,
  handleSendsms,
  newFromNumber,
}) {
  return (
    <Flex justify="end" className="h-100 w-100" gap={12}>
      {(conversations?.length < 1 || !integId) && !chatView && (
        <div>
          <FromNumberComponent
            customClasses={"fromNumber-conv"}
            fromNumber={newFromNumber}
            handleFromNumberChange={handleFromNumberChange}
            savedNumbersList={savedNumbersList}
            setSavedNumbersList={setSavedNumbersList}
            disabled={false}
          />
        </div>
      )}
      <SendMessageButton handleSendsms={handleSendsms} sendBtnLoading={sendBtnLoading} antIcon={antIcon} />
    </Flex>
  );
}

const ChatViewHeader = (props) => {
  const {
    newFromNumber,
    setNewFromNumber,
    setSavedNumbersList,
    savedNumbersList,
    setToNumberChatView,
    phoneFieldConfiguration,
    setPhoneFieldConfiguration,
    selectedContactId,
  } = props;
  const [toNumber, setToNumber] = useState("");
  const [phoneFields, setPhoneFields] = useState([]);
  const handleFromNumberChange = (e) => {
    selectedContactId.current = DEFAULT_CONTACT_ID;
    setNewFromNumber(e);
  };
  const [debouncedToNumber] = useDebounce(toNumber, 500);
  useEffect(() => {
    selectedContactId.current = DEFAULT_CONTACT_ID;
    setToNumberChatView(debouncedToNumber);
  }, [debouncedToNumber]);

  return (
    <Flex justify="center" className="hs-h-min-100-px hs-h-100-px w-100">
      <div className="hs-w-95">
        <Row className="w-100 hs-border-bottom h-50">
          <Col className="d-flex align-items-center justify-content-between" span={3}>
            <div>From</div>
            <div>:</div>
          </Col>
          <Col span={21} className="d-flex align-items-center">
            <FromNumberComponent
              chatView={true}
              fromNumber={newFromNumber}
              handleFromNumberChange={handleFromNumberChange}
              savedNumbersList={savedNumbersList}
              setSavedNumbersList={setSavedNumbersList}
              disabled={false}
              defaultSelectFirstOption={true}
              variant="borderless"
              customClasses="hs-h-44-px"
              selectRecentFromNumber={true}
            />
          </Col>
        </Row>
        <Row className="w-100 hs-border-bottom h-50">
          <Col className="d-flex align-items-center justify-content-between" span={3}>
            <div>To</div>
            <div>:</div>
          </Col>
          <Col span={19} className="d-flex align-items-center justify-content-start">
            <SendSMSToNumberComponent
              toNumber={toNumber}
              setToNumber={setToNumber}
              savedNumbersList={savedNumbersList}
              setSavedNumbersList={setSavedNumbersList}
              disabled={false}
              defaultSelectFirstOption={true}
              setPhoneFields={setPhoneFields}
              phoneFieldConfiguration={phoneFieldConfiguration}
              setPhoneFieldConfiguration={setPhoneFieldConfiguration}
            />
          </Col>
          <Col span={2} className="d-flex align-items-center justify-content-center">
            <ConfigurePhone
              phoneFieldConfiguration={phoneFieldConfiguration}
              setPhoneFieldConfiguration={setPhoneFieldConfiguration}
              phoneFields={phoneFields}
              toNumber={toNumber}
              setToNumber={setToNumber}
              setPhoneFields={setPhoneFields}
            />
          </Col>
        </Row>
      </div>
    </Flex>
  );
};

function AssigneePopover({ appProperties, contactId, setNumbersList, disabled = false }) {
  const { conversationContext, setConversationContext } = useContext(ConvContext);
  const { assignUserForContact } = useUsers();
  const [loading, setLoading] = React.useState(false);

  const currentAssignedUser = useMemo(() => {
    return conversationContext[contactId]?.contactOwnerEntityId;
  }, [conversationContext, contactId]);

  // TODO: Handle multiple assigned users
  const assignees = useMemo(
    () => [
      ...(appProperties?.licenseObj?.allLicensedUserDetails?.map((user) => ({
        ...user,
        label: <UserProfileCard size="small" user={user} disabled={disabled || !contactId || contactId === DEFAULT_CONTACT_ID} />,
        labelText: user?.firstName + " " + (user?.lastName ? user?.lastName + " (" + user?.email + ")" : "(" + user?.email + ")"),
        value: user.osyncUserId,
        userData: user,
      })) || []),
    ],
    [appProperties?.licenseObj?.allLicensedUserDetails, contactId, disabled]
  );

  const handleAssignContact = async (osyncUserId) => {
    setLoading(true);
    try {
      let assignContactToUserResponse = null;

      if (currentAssignedUser === osyncUserId) {
        HNotification.notify({ type: "warning", message: "Warning", description: "Contact already assigned to this user" });
        return;
      }

      assignContactToUserResponse = await assignUserForContact(contactId, osyncUserId, appProperties);
      if (assignContactToUserResponse) {
        setConversationContext((prev) => ({
          ...prev,
          [contactId]: {
            ...(prev?.[contactId] || {}),
            conversations: [...(prev?.[contactId]?.conversations || []), ...(assignContactToUserResponse || [])],
            contactOwnerEntityId: osyncUserId,
          },
        }));
        if (setNumbersList) {
          setNumbersList((prevList) =>
            prevList.map((item) => (item.contactId === contactId ? { ...item, contactOwnerEntityId: osyncUserId } : item))
          );
        }

        successNotification(`Contact assigned successfully`);
      } else {
        throw new Error(`Failed to assign contact`);
      }
    } catch (error) {
      HNotification.notify({ type: "error", message: "Error", description: error?.message || "Failed to assign" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AssigneeSelect
      disabled={disabled || !contactId || contactId === DEFAULT_CONTACT_ID}
      appProperties={appProperties}
      currentSelectedUser={currentAssignedUser}
      handleOnSelect={handleAssignContact}
      usersList={assignees}
      loading={loading}
    />
  );
}

const AssigneeSelect = ({ appProperties, currentSelectedUser, handleOnSelect, usersList, loading, disabled = false }) => {
  const isAssignedUserExistsInAllLicensedUserDetails = useMemo(() => {
    return appProperties?.licenseObj?.allLicensedUserDetails?.some((user) => user.osyncUserId === currentSelectedUser);
  }, [appProperties?.licenseObj?.allLicensedUserDetails, currentSelectedUser]);

  return (
    <HSelect
      data-cy="conversation-header-assignee-select"
      bordered={false}
      size="medium"
      customProps={{
        value: isAssignedUserExistsInAllLicensedUserDetails ? currentSelectedUser : null,
        suffixIcon: <div className="actionIconsSprite smallSize-fromNumberDropDownIcon" />,
        onSelect: (value) => handleOnSelect(value),
        className: "hs-h-35-px",
      }}
      popupMatchSelectWidth={false}
      placeholder={
        <div className="d-flex align-items-center gap-2">
          <div className="actionIconsSprite2 unassigned-icon" />
          Unassigned
        </div>
      }
      options={usersList}
      loading={loading}
      disabled={loading || disabled}
      rootClassName="assignee-dropdown"
    />
  );
};

const ConversationHeader = ({
  toNumber,
  savedNumbersList,
  newFromNumber,
  setShowChatOverlay,
  appProperties,
  showContactInfo,
  setShowContactInfo,
  selectedContactId,
  setNumbersList,
  setShowSwitch,
  setIsChannelPresent,
  setSelectedContact,
  greaterThanLargeScreen
}) => {
  const { conversationContext, setConversationContext } = useContext(ConvContext);
  const [currentFlag, setCurrentFlag] = useState(null);
  const [rolesProperties] = useRolesContext();
  const navigate = useNavigate();
  const location = useLocation();

  const { contactId: conatctIdFromUrl } = useParams();


  // Initialize flag from conversationContext
  useEffect(() => {
    const currentContactInfo = conversationContext?.[selectedContactId?.current] || {};
    if (currentContactInfo?.userFlagColorMap && appProperties?.currentUser?.osyncUserId) {
      const userFlag = currentContactInfo.userFlagColorMap[appProperties.currentUser.osyncUserId];
      setCurrentFlag(userFlag || null);
    } else {
      setCurrentFlag(null);
    }
  }, [conversationContext, selectedContactId?.current, appProperties?.currentUser?.osyncUserId]);

  const handleFlagChange = (newFlag) => {
    setCurrentFlag(newFlag);
    // Update conversationContext to reflect the change
    if (setNumbersList) {
      setNumbersList((prevList) =>
        prevList.map((item) => {
          if (item.contactId === selectedContactId?.current) {
            const updatedUserFlagColorMap = {
              ...(item.userFlagColorMap || {}),
              [appProperties?.currentUser?.osyncUserId]: newFlag,
            };

            // Remove the key if flag is null
            if (!newFlag) {
              delete updatedUserFlagColorMap[appProperties?.currentUser?.osyncUserId];
            }

            return {
              ...item,
              userFlagColorMap: updatedUserFlagColorMap,
            };
          }
          return item;
        })
      );
    }
    // Also update conversationContext
    setConversationContext((prev) => ({
      ...prev,
      [selectedContactId?.current]: {
        ...(prev[selectedContactId?.current] || {}),
        userFlagColorMap: {
          ...(prev[selectedContactId?.current]?.userFlagColorMap || {}),
          [appProperties?.currentUser?.osyncUserId]: newFlag,
        },
      },
    }));
  };

  useEffect(() => {
    let findNumber = savedNumbersList?.find((entry) => entry.phoneNumber === newFromNumber);
    let integ = findNumber?.integId;
    if (
      findNumber?.isMessagingService === "true" ||
      findNumber?.isAlphaSender === "true" ||
      appProperties.providerCommonObj.isShowAllFeatureNotSupported
    ) {
      setShowSwitch(false);
    } else if (appProperties.providerCommonObj.isShowAllFeatureNotSupported === false) {
      setShowSwitch(true);
    }
  }, [savedNumbersList, newFromNumber, appProperties.providerCommonObj]);

  const hasInboxEditAccess = useMemo(() => {
    return checkUserHasEditAccess(rolesProperties?.currentRoleDetails, "Inbox");
  }, [rolesProperties]);

  return (
    <>
      <div className={`inbox-header px-3 border-bottom h-100`}>
        <Flex justify="space-between" align="center" className="h-100">
          <Flex gap={greaterThanLargeScreen ? 14 : 8} align="center">
            <div
              className="d-md-none"
              onClick={() => {
                setIsChannelPresent(false);
                setSelectedContact(null);
                selectedContactId.current = DEFAULT_CONTACT_ID;
                setShowChatOverlay(false);
                if (conatctIdFromUrl) {
                  deselectCurrentContact(navigate, location);
                }
              }}
            >
              <div className="actionIconsSprite back-icon" />
            </div>
            <AssigneePopover disabled={!hasInboxEditAccess} appProperties={appProperties} contactId={selectedContactId.current} setNumbersList={setNumbersList} />
            <ConversationStatusSelector disabled={!hasInboxEditAccess} contactId={selectedContactId.current} />
          </Flex>
          <div>
            <Space>
              <FlagSelector
                disabled={selectedContactId.current === DEFAULT_CONTACT_ID || !hasInboxEditAccess}
                size="large"
                contactId={selectedContactId.current}
                currentFlag={currentFlag}
                onFlagChange={handleFlagChange}
                appProperties={appProperties}
                className="hover-flag"
              />
              <div
                className={`actionIconsSprite2 ${showContactInfo ? "show-contact-info-icon-opened" : "show-contact-info-icon-closed"}`}
                onClick={() => setShowContactInfo((prev) => !prev)}
              />
            </Space>
          </div>
        </Flex>
      </div>
    </>
  );
};
const ConversationMessages = React.memo(
  ({
    contactInfo,
    conversations,
    scrollableDivRef,
    updateUnreadMessages,
    clearAllUnreadMessages,
    unReadMessages,
    integId,
    sendsms,
    appProperties,
    handleSendSmsResp,
    toNumber,
    toNumberChatView,
    balanceCredits,
    chatView,
    previousScrollHeight,
    fetchConversationLoading,
    fetchByList,
    selectedContactId,
    channelRedirectUrl,
    setConversationContext,
    setNumbersList,
    handleFocusNote,
    showScrollButton,
    setShowScrollButton,
    isAtBottomRef,
    isFromFetchApi,
  }) => {
    useEffect(() => {
      const currentContactId = selectedContactId.current;
      return () => {
        setConversationContext((prev) => {
          return {
            ...prev,
            [currentContactId]: {
              ...prev?.[currentContactId],
              fileList: prev?.[currentContactId]?.fileList?.filter((file) => file.status === "done"),
            },
          };
        });
      };
    }, []);

    const [hoveredMessageIndex, setHoveredMessageIndex] = useState(null);
    const [markAsUnreadLoading, setMarkAsUnreadLoading] = useState(false);
    const [hovered, setHovered] = useState(false);
    const { fetchData } = useHttp();

    const handleMarkAsUnread = async (messageIndex) => {
      if (markAsUnreadLoading) return;

      setMarkAsUnreadLoading(true);

      try {
        // Count messages from this message to the end (only count received messages)
        let unreadCount = 0;

        // We count all received messages from the clicked message up to the end
        // This ensures we correctly mark all messages under the clicked message as unread
        for (let i = messageIndex; i < conversations.length; i++) {
          if (conversations[i]?.direction === MessageDirection.INBOUND) {
            // Only count received messages (direction === 1)
            unreadCount++;
          }
        }

        if (unreadCount === 0) {
          setMarkAsUnreadLoading(false);
          return;
        }

        const contactId = selectedContactId.current;

        // Call API to mark messages as unread
        await fetchData(`omessage/conversation/${contactId}/read?unreadCount=${unreadCount}`, "POST", null, appProperties);

        // Update numberList unreadCount
        setNumbersList((prev) => prev.map((item) => (item.contactId === contactId ? { ...item, unreadCount } : item)));

        // Update conversationContext for this contact
        setConversationContext((prev) => {
          const old = prev[contactId] || {};

          // Mark messages as unread from messageIndex to the end (only received messages)
          const updatedConversations = conversations.map((msg, i) => {
            if (i >= messageIndex && msg?.direction === MessageDirection.INBOUND) {
              // Mark received messages as unread
              return { ...msg, isRead: false };
            }
            return msg;
          });

          return {
            ...prev,
            [contactId]: {
              ...old,
              conversations: updatedConversations,
            },
          };
        });

        setTimeout(() => {
          successNotification(`Marked the message(s) as unread`);
        }, 300);
      } catch (e) {
        HNotification.notify({ type: "error", message: "Error", description: "Failed to mark as unread" });
        console.error("Error marking as unread:", e);
      } finally {
        setMarkAsUnreadLoading(false);
      }
    };

    const handleRetrySendsms = (obj) => {
      let rightPhoneObj = appProperties.installedApps?.find((obj) => obj?.integProps?.integId === integId);
      if (rightPhoneObj?.right?.auth?.ignoreAuthorizedCheck || rightPhoneObj?.right?.auth?.authorized) {
        let newUUID = crypto.randomUUID();
        var idObject = {
          ...obj,
          createdTime: new Date().toISOString(),
          messageStatus:
            !CREDIT_BASED_SERVICES.includes(rightPhoneObj?.right?.service_name) ||
              (CREDIT_BASED_SERVICES.includes(rightPhoneObj?.right?.service_name) && balanceCredits > 0.5)
              ? "queued"
              : "failed",
          errorMessage:
            CREDIT_BASED_SERVICES.includes(rightPhoneObj?.right?.service_name) && balanceCredits < 0.5 ? (
              <span>
                Message sending failed due to low credits. Please
                <BuyFunds />.
              </span>
            ) : null,
          tempMessageId: newUUID,
        };
        setConversationContext((prev) => ({
          ...prev,
          [selectedContactId.current]: {
            ...prev[selectedContactId.current],
            conversations: [...(prev[selectedContactId.current]?.conversations || []), idObject],
          },
        }));

        const mediaFileList = obj.attachments
          ? obj.attachments.map((file) => {
            return {
              name: file.name,
              url: file.url,
              contentType: file.type,
              size: file.size,
            };
          })
          : [];
        const body = {
          from: obj.sender_id,
          to: chatView ? toNumberChatView : toNumber,
          message: obj.message,
          pageId: obj.sender_id,
          tempMessageId: newUUID,
          messageBody: obj.message,
          mediaFileList: mediaFileList,
        };
        if (
          !CREDIT_BASED_SERVICES.includes(rightPhoneObj?.right?.service_name) ||
          (CREDIT_BASED_SERVICES.includes(rightPhoneObj?.right?.service_name) && balanceCredits > 0.5)
        ) {
          sendsms(integId, body)
            .then(function (response) {
              if (response) {
                response = JSON.parse(response);
                handleSendSmsResp(response, newUUID, idObject);
              }
            })
            .catch((err) => {
              setConversationContext((prev) => {
                var existingIndex = prev[selectedContactId?.current]?.conversations?.findIndex((item) => item?.tempMessageId === newUUID);
                if (existingIndex !== -1) {
                  const newList = [...prev[selectedContactId?.current]?.conversations];
                  let messageData = {
                    ...idObject,
                    class: "input-error",
                    messageStatus: "failed",
                  };
                  newList[existingIndex] = messageData;
                  prev[selectedContactId?.current].conversations = newList;
                }
                return prev;
              });
            });
        }
      } else {
        if (rightPhoneObj?.right) {
          HNotification.notify({
            type: "warning",
            message: "Service not authorized",
            description: `${rightPhoneObj?.right?.service_display_name} service is not authorized !`,
            button: true,
            buttonText: "Authorize",
            buttonLink: `/channels/textsms/${rightPhoneObj?.right?.service_name}?` + channelRedirectUrl,
          });
        }
      }
    };

    const handleMouseEnter = (index) => {
      setHoveredMessageIndex(index);
    };

    const handleMouseLeave = () => {
      setHoveredMessageIndex(null);
    };

    function mapMessages(data) {
      return (
        <>
          {data?.map((obj, index) => {
            const quotedReplyObject = obj?.associatedMessages?.find((associatedMessage) => associatedMessage?.type === "QUOTED_REPLY");
            return (
              <div
                key={index}
                onMouseEnter={() => {
                  // Only show mark as unread for received messages (direction === 1)
                  // And only for the last 10 received messages
                  if (obj?.isRead && obj?.direction === MessageDirection.INBOUND) {
                    // Count how many received messages are in the conversation
                    const receivedMessages = conversations.filter((msg) => msg?.direction === MessageDirection.INBOUND);
                    // Get position of this message in the received messages array
                    const receivedIndex = receivedMessages.indexOf(obj);
                    // Only show for the last 10 received messages
                    if (receivedIndex >= receivedMessages.length - 10) {
                      setHoveredMessageIndex(index);
                    }
                  }
                }}
                onMouseLeave={() => setHoveredMessageIndex(null)}
              >
                {/* Show date separator when date changes between messages */}
                {(index === 0 || formatDate(obj?.createdTime) !== formatDate(data[index - 1]?.createdTime)) && (
                  <div className="d-flex align-items-center justify-content-center mb-2 mt-2">
                    <div
                      data-cy="message-date-separator"
                      className="p-1 hs-border-20 px-3"
                      style={{
                        backgroundColor: "#F4F5FA",
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#666",
                      }}
                    >
                      {formatDate(obj?.createdTime)}
                    </div>
                  </div>
                )}

                {/* Show "Unread Messages" label before the first unread message, but only for received messages */}
                {obj?.isRead === false &&
                  obj?.direction === MessageDirection.INBOUND &&
                  (index === 0 || (index > 0 && data[index - 1]?.isRead !== false)) &&
                  obj === data.find((message) => message?.isRead === false && message?.direction === MessageDirection.INBOUND) && (
                    <div
                      id="first-unread-message-label"
                      className="d-flex align-items-center justify-content-center mb-2"
                      data-cy="first-unread-message-label"
                    >
                      <div
                        className="p-1 rounded-5 ps-3 pe-3"
                        style={{
                          backgroundColor: "#FFF3CD",
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#856404",
                        }}
                      >
                        Unread Messages
                      </div>
                    </div>
                  )}

                <div
                  className={`d-flex py-1 ${obj?.direction === MessageDirection.OUTBOUND ? "ms-auto" : "me-auto"} ${
                    obj?.direction === MessageDirection.NONE ? "w-100" : "hs-mw-85"
                  }`}
                  style={{
                    alignItems:
                      obj?.direction === MessageDirection.OUTBOUND ? "end" : obj?.direction === MessageDirection.NONE ? "center" : "start",
                  }}
                  data-cy="conversation-message-container"
                >
                  {obj?.direction === MessageDirection.NONE && obj?.status?.includes("notes_") ? (
                    <div className="d-flex w-100 justify-content-center align-items-center hs-fs-12">
                      <div className="hs-message-area-warning hs-h-32-px hs-border-20 d-flex align-items-center justify-content-center px-3 gap-2">
                        <div className="actionIconsSprite2 inbox-notes-icon d-lg-block d-none"></div>
                        <span>
                          {`${
                            obj?.osyncUserId === appProperties.currentUser?.osyncUserId
                              ? "You"
                              : (() => {
                                  const user = appProperties?.licenseObj?.allLicensedUserDetails?.find(
                                    (user) => user?.osyncUserId === obj?.osyncUserId,
                                  );
                                  return user?.firstName || user?.lastName || user?.fullName;
                                })()
                          } ${
                            obj?.status === "notes_added"
                              ? "added a note"
                              : obj?.status === "notes_updated"
                                ? "updated a note"
                                : "deleted a note"
                          }`}
                        </span>
                        <span className="hs-color-red-1">{formatTime(obj?.createdTime)}</span>
                        {obj?.status !== "notes_deleted" && (
                          <div
                            className="d-flex justify-content-end text-decoration-underline cursor-pointer ms-1"
                            onClick={() => handleFocusNote(obj, index)}
                          >
                            View
                          </div>
                        )}
                      </div>
                    </div>
                  ) : obj?.direction === MessageDirection.NONE && obj?.status?.includes("conversation_assigned") ? (
                    <div className="d-flex w-100 justify-content-center align-items-center hs-fs-12">
                      <div className="hs-color-bg-light-violet hs-color-violet hs-h-32-px hs-border-20 d-flex align-items-center justify-content-center px-3 gap-2">
                        <div className="actionIconsSprite2 small-blue-user-icon" />
                        <span>{obj?.message}</span>
                      </div>
                    </div>
                  ) : obj?.direction === MessageDirection.NONE &&
                    CONVERSATION_STATUS_UPDATES_INBOX_STATUSES.includes(obj?.status) ? (
                    <div className="d-flex w-100 justify-content-center align-items-center hs-fs-12">
                      <div className={`${obj?.status?.includes("closed") ? "hs-bg-pale-blue-1" : "hs-bg-green-2"} ${obj?.status?.includes("closed") ? "hs-color-dark-grey-2" : "whatsapp-color"} hs-h-32-px d-flex align-items-center justify-content-center w-100 gap-1`}>
                        {obj?.status?.includes("closed") ? <div className="actionIconsSprite2 cursor-default conversation-closed-icon" /> : <div className="actionIconsSprite2 cursor-default conversation-open-icon" />}
                        <span>
                          {obj?.message} at {formatTime(obj?.createdTime)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="d-flex justify-content-end align-items-end w-100"
                      style={{
                        flexFlow: obj?.direction === MessageDirection.OUTBOUND ? "row" : "row-reverse",
                      }}
                    >
                      <div className="d-flex flex-column align-items-end position-relative w-auto">
                        {obj?.attachments?.length > 0 ? (
                          <div className="position-relative shadow-sm rounded p-2">
                            {quotedReplyObject ? <QuotedReply quotedReplyObject={quotedReplyObject} /> : null}
                            <EmojiOverlay conversationObject={obj} />
                            {obj?.attachments.map((attachment, index) => (
                              <>
                                {(attachment.type?.startsWith("application/") || attachment.type?.startsWith("text/")) && (
                                  <div className="h-100 w-100 mb-2">
                                    <a key={index} href={attachment.url} target="_blank" rel="noopener noreferrer" className="pe-2 pdfAttch">
                                      <div className="d-flex align-items-center justify-content-start">
                                        <span
                                          className={`${attachment.type === "application/pdf" ? "pdf-icon" : "doc-icon"} staticIconsSprite`}
                                        ></span>
                                        {attachment.name}
                                      </div>
                                    </a>
                                  </div>
                                )}
                                {attachment.type?.startsWith("audio/") && (
                                  <div>
                                    <audio
                                      key={index}
                                      src={attachment.url}
                                      controls
                                      className="mt-1"
                                      style={{
                                        borderRadius: 12,
                                      }}
                                    />
                                  </div>
                                )}
                              </>
                            ))}
                            {obj?.attachments.some(
                              (attachment) =>
                                attachment.type?.startsWith("application/") ||
                                attachment.type?.startsWith("text/") ||
                                attachment.type?.startsWith("audio/"),
                            ) && <DateAndStatus conversationObject={obj} />}
                          </div>
                        ) : null}

                        {obj?.attachments?.length > 0 &&
                        obj?.attachments?.some(
                          (attachment) => attachment?.type?.startsWith("image/") || attachment?.type?.startsWith("video/"),
                        ) ? (
                          <>
                            <div
                              className={`position-relative image-container ${["failed", "undelivered"].includes(obj?.messageStatus || obj?.status) ? "image-failed" : ""
                                }`}
                            >
                              {quotedReplyObject ? <QuotedReply quotedReplyObject={quotedReplyObject} key={"3018"} /> : null}
                              <EmojiOverlay conversationObject={obj} />
                              <div className={obj.attachments.length > 1 ? "conv-attachments" : ""}>
                                {obj?.attachments.map((attachment, index) => (
                                  <>
                                    {attachment.type?.startsWith("image/") && (
                                      <div className="image-background">
                                        <Image
                                          key={index}
                                          src={attachment.url}
                                          preview={{
                                            mask: false,
                                            toolbarRender: () => null,
                                          }}
                                          width={getMediaDimension(obj, "22rem")}
                                          className="chatImage mt-1"
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = MediaError;
                                          }}
                                          style={{
                                            minHeight: getMediaDimension(obj, "16rem"),
                                            height: getMediaDimension(obj, "16rem"),
                                          }}
                                        />
                                      </div>
                                    )}
                                    {attachment.type?.startsWith("video/") && (
                                      <div>
                                        <video
                                          key={index}
                                          src={attachment.url}
                                          controls
                                          width={getMediaDimension(obj, "16rem")}
                                          className="mt-1"
                                          style={{
                                            borderRadius: 12,
                                            margin: -1,
                                          }}
                                        />
                                      </div>
                                    )}
                                  </>
                                ))}
                              </div>
                              {obj?.message &&
                                obj?.attachments?.some(
                                  (attachment) => attachment?.type?.startsWith("image/") || attachment?.type?.startsWith("video/"),
                                ) && (
                                  <pre
                                    className={obj?.message ? "p-1" : ""}
                                    style={{
                                      fontSize: 14,
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                      position: "relative",
                                      maxWidth:
                                        obj.attachments.length === 1 && !chatView
                                          ? "16rem"
                                          : obj.attachments.length > 1 && chatView
                                            ? "14rem"
                                            : obj.attachments.length === 1 && chatView
                                              ? "11rem"
                                              : "22rem",
                                    }}
                                  >
                                    {convertURLsToLinks(obj, true)}
                                  </pre>
                                )}
                              <DateAndStatus conversationObject={obj} />
                            </div>
                            {["failed", "undelivered"].includes(obj?.messageStatus || obj?.status) && (
                              <div
                                className="message-retry"
                                onClick={() => {
                                  handleRetrySendsms(obj);
                                }}
                              >
                                <div className="actionIconsSprite message-retry-icon "></div>{" "}
                              </div>
                            )}
                          </>
                        ) : null}
                        {(obj?.message || obj?.components) &&
                          !obj?.attachments?.some(
                            (attachment) => attachment?.type?.startsWith("image/") || attachment?.type?.startsWith("video/"),
                          ) &&
                          (obj?.components ? (
                            <>
                              <div className="position-relative">
                                {quotedReplyObject ? <QuotedReply quotedReplyObject={quotedReplyObject} key={"3113"} /> : null}
                                <EmojiOverlay conversationObject={obj} />
                                <ComponentPreview source={"conversation"} componentsData={obj?.components} scrollable={false} />
                              </div>
                              <DateAndStatus conversationObject={obj} />
                            </>
                          ) : (
                            <div
                              className={`w-100 ${
                                ["failed", "undelivered"].includes(obj?.messageStatus || obj?.status)
                                  ? "failedMessage"
                                  : obj?.direction === MessageDirection.OUTBOUND
                                    ? "deliveredMessage"
                                    : obj?.direction === MessageDirection.INBOUND && obj?.isRead === false
                                      ? "unreadReceivedMessage"
                                      : "receivedMessage"
                              }  ${obj?.class ?? ""}`}
                              data-cy={
                                obj?.direction === MessageDirection.INBOUND
                                  ? obj?.isRead === false
                                    ? "unread-received-message"
                                    : "received-message"
                                  : undefined
                              }
                            >
                              {quotedReplyObject ? <QuotedReply quotedReplyObject={quotedReplyObject} key={"3134"} /> : null}
                              <EmojiOverlay conversationObject={obj} />
                              <div
                                style={{
                                  position: "relative",
                                }}
                                className={obj?.message && "p-2"}
                              >
                                <pre
                                  style={{
                                    fontSize: 14,
                                    position: "relative",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    textAlign: `start`,
                                  }}
                                >
                                  {convertURLsToLinks(obj, false)}
                                </pre>
                                {["failed", "undelivered"].includes(obj?.messageStatus || obj?.status) && (
                                  <div
                                    className="message-retry"
                                    onClick={() => {
                                      handleRetrySendsms(obj);
                                    }}
                                  >
                                    <div className="actionIconsSprite message-retry-icon "></div>{" "}
                                  </div>
                                )}
                                <DateAndStatus conversationObject={obj} />
                              </div>
                            </div>
                          ))}
                        {obj?.direction === MessageDirection.INBOUND && (
                          <div
                            className={`d-flex justify-content-start mb-1 cursor-pointer position-absolute mark-as-unread ${obj?.isRead === true && hoveredMessageIndex === index ? "visible" : "invisible"
                              }`}
                            onClick={() => handleMarkAsUnread(conversations.indexOf(obj))}
                          >
                            {markAsUnreadLoading ? (
                              <Spin size="small" />
                            ) : (
                              <Tooltip title="Mark as unread">
                                <div
                                  className="d-flex align-items-center "
                                  style={{ fontSize: "14px", paddingLeft: "8px" }}
                                  onMouseEnter={() => setHovered(true)}
                                  onMouseLeave={() => setHovered(true)}
                                >
                                  <div
                                    className={`actionIconsSprite ${hovered ? "mark-as-unread-color-icon" : "mark-as-unread-icon"} me-1`}
                                    style={{ transform: "scale(1)" }}
                                    data-cy="mark-as-unread-icon"
                                  />
                                </div>
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </div>

                      <div
                        className="ms-2 me-2"
                        style={{
                          borderRadius: 8,
                          height: 30,
                          width: 30,
                          marginBottom: 1,
                          minWidth: 30,
                          background: !obj?.reply_by ? "" : obj?.reply_by === data[index + 1]?.reply_by ? "white" : "#EBF5ED",
                        }}
                      >
                        {obj?.direction === MessageDirection.OUTBOUND ? (
                          <ConversationAvatar
                            name={obj?.reply_by}
                            imageUrl={obj?.sender_url}
                            shouldRender={
                              !obj?.reply_by
                                ? data?.[index + 1]?.osyncUserId && data?.[index + 1].direction === MessageDirection.OUTBOUND
                                  ? obj?.osyncUserId !== data?.[index + 1]?.osyncUserId
                                  : true
                                : data?.[index + 1]?.reply_by && data?.[index + 1].direction === MessageDirection.OUTBOUND
                                  ? obj?.reply_by !== data?.[index + 1]?.reply_by
                                  : true
                            }
                            customClasses={"hs-color-text-green hs-bg-light-green3"}
                            messageOwnerDetails={licensedUsersMap?.get(obj?.osyncUserId)}
                          />
                        ) : (
                          <ConversationAvatar
                            name={contactInfo?.firstName}
                            imageUrl={contactInfo?.avatar || contactInfo?.avatar_url}
                            shouldRender={obj?.direction !== data[index + 1]?.direction}
                            tooltipTitle={obj?.sender_id}
                            customClasses={"hs-bg-light-orange hs-color-dark-orange"}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </>
      );

      function getMediaDimension(obj) {
        return obj.attachments.length === 1 && !chatView ? "256px" : obj.attachments.length > 1 && chatView ? "112px" : "176px";
      }
    }

    const licensedUsersMap = useMemo(() => {
      const map = new Map();
      appProperties?.allLicensedUserDetails?.forEach((user) => {
        map.set(user?.osyncUserId, user);
      });
      return map;
    }, [appProperties?.allLicensedUserDetails]);


    const handleInboxDivScroll = useDebouncedCallback(
      () => {
        const scrollableDiv = scrollableDivRef?.current;
        if (!scrollableDiv) return;
        // Check if user is at bottom (within 100px)
        const isBottom = scrollableDiv.scrollHeight - scrollableDiv.scrollTop <= scrollableDiv.clientHeight + 100;
        isAtBottomRef.current = isBottom;

        // If user scrolls back to bottom, hide the button
        if (isBottom) {
          setShowScrollButton(false);
        }

        if (scrollableDiv.scrollTop < 300 && !isFromFetchApi.current && !fetchConversationLoading) {
          previousScrollHeight.current = scrollableDiv.scrollHeight;
          scrollableDiv.style.scrollBehavior = "auto";
          fetchByList(integId, !chatView, false, "scroll");
        }
      },
      300,
      { maxWait: 1000 }
    );

    return (
      <div className="conversation-and-new-message-button-container">
        <div
          className="overflow-auto mt-auto h-auto"
          style={{ scrollBehavior: "auto" }}
          ref={scrollableDivRef}
          onScroll={handleInboxDivScroll}
          data-cy="conversation-messages-container-area"
          onClick={() => {
            if (unReadMessages?.length > 0) {
              clearAllUnreadMessages();
            }
          }}
        >
          {fetchConversationLoading && (
            <div className="w-100 d-flex justify-content-center">
              <div className="actionIconsSprite dropDownLoading" />
            </div>
          )}
          {mapMessages(conversations)}
        </div>
        {showScrollButton && (
          <Button
            className="scroll-to-bottom-btn d-flex align-items-center justify-content-center position-absolute"
            onClick={() => {
              if (scrollableDivRef.current) {
                scrollableDivRef.current.scrollTop = scrollableDivRef.current.scrollHeight;
                setShowScrollButton(false);
              }
            }}
          >
            New Message <ArrowDownOutlined />
          </Button>
        )}
      </div>
    );
  }
);

const QuotedReply = React.memo(({ quotedReplyObject }) => {
  return (
    <div className="p-1" key={quotedReplyObject?.id}>
      <Flex className="hs-border-bottom-light-blue-1 p-2" gap={4} align="flex-start" key={`${quotedReplyObject?.id}-flex`}>
        {/* Purple arrow icon on the left */}
        <div className="d-flex align-items-top justify-content-center">
          <div className="actionIconsSprite2 quote-reply-icon" />
        </div>

        {/* Image container - rounded square with teal background if image exists */}
        {quotedReplyObject?.attachments && (
          <div>
            <ReusableFilePreview fileList={mapToMediaList(quotedReplyObject?.attachments, true)} shape="square" />
          </div>
        )}

        {quotedReplyObject?.components && (
          <div>
            <ComponentPreview source={"conversation"} componentsData={quotedReplyObject?.components} />
          </div>
        )}

        {/* Text content with expandable "read more" */}
        {quotedReplyObject?.body && (
          <div className="flex-grow-1 my-auto mx-auto">
            <Typography.Paragraph
              ellipsis={{
                rows: 2,
                expandable: true,
                symbol: "read more",
              }}
              className="hs-mb-0 hs-color-md-grey"
            >
              {quotedReplyObject?.body || ""}
            </Typography.Paragraph>
          </div>
        )}
      </Flex>
    </div>
  );
});


function EmojiOverlay({ conversationObject }) {
  const emojiObject = conversationObject?.associatedMessages?.find((associatedMessage) => associatedMessage?.type === "REACTION");
  if (!emojiObject) {
    return <></>;
  }
  return (
    <div
      className={`conversation-emoji-overlay ${
        conversationObject?.direction === MessageDirection.OUTBOUND ? "conversation-emoji-overlay-left" : "conversation-emoji-overlay-right"
      } hs-border-dark-grey d-flex align-items-center justify-content-center`}
    >
      {emojiObject?.body}
    </div>
  );
}

const NoConversations = ({
  navigate,
  channelRedirectUrl,
  chatView,
  appProperties,
  isContactAvailable,
  newToNumberInputRef,
  setNumbersList,
  setImportContactsNotificationOpen,
  unipilePhoneNumberObj,
}) => {
  const showImportModal = !chatView && !isContactAvailable && unipilePhoneNumberObj?.phoneNumber;
  const [showImportWhatsAppModal, setShowImportWhatsAppModal] = useState(false);
  return (
    <div className="d-flex flex-column align-items-center justify-content-center">
      {!appProperties?.serviceInstalled ? (
        <div className="d-flex flex-column align-items-center noConvo-info text-center hs-max-w-300-px">
          <div className="staticIconsSprite chatIcon-sprite"></div>
          <span style={{ fontSize: chatView ? 14 : 16, color: "#111827", fontWeight: 600 }}>{INFO_MESSAGES.INBOX.STAY_CONNECTED} </span>
          <span style={{ fontSize: chatView ? 12 : 14, color: "#475467" }}>
            Connect your channel and begin typing a contact name or number above to initiate a new message.
          </span>
        </div>
      ) : showImportModal ? (
        <div className="d-flex flex-column align-items-center noConvo-info text-center hs-max-w-300-px">
          <div className="d-flex flex-column align-items-center">
            <ImportChat
              appProperties={appProperties}
              setNumbersList={setNumbersList}
              unipilePhoneNumberObj={unipilePhoneNumberObj}
              showImportWhatsAppModal={showImportWhatsAppModal}
              setShowImportWhatsAppModal={setShowImportWhatsAppModal}
              onSkip={() => {
                setShowImportWhatsAppModal(false);
              }}
            />
            <div className="cardsSprite check-icon"></div>
            <div className="hs-color-BlackRussian fw-bold hs-fs-16 mt-1">You are all set</div>
            <span className="hs-color-dark-grey hs-fs-12 my-2">Your WhatsApp number is connected. You can now start messaging</span>
          </div>
          <div className="d-flex gap-2 mt-2 align-items-center justify-content-center">
            <HButton type="outline" variant="cancel" size="m" onClick={() => setShowImportWhatsAppModal(true)}>
              Import from WhatsApp
            </HButton>

            <HButton
              type="primary"
              size="m"
              onClick={() => {
                newToNumberInputRef && newToNumberInputRef?.current?.focus?.();
              }}
            >
              New Message
            </HButton>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column align-items-center noConvo-info text-center hs-max-w-300-px">
          <div className="staticIconsSprite chatIcon-sprite"></div>
          <span style={{ fontSize: chatView ? 14 : 16, color: "#111827", fontWeight: 600 }}> {INFO_MESSAGES.INBOX.STAY_CONNECTED} </span>
          {!chatView && (
            <span
              style={{
                fontSize: chatView ? 12 : 14,
                color: "#475467",
              }}
            >
              {INFO_MESSAGES.INBOX.STAY_CONNECTED_TO_INITIATE_NEW_MESSAGE}
            </span>
          )}
        </div>
      )}
      {!appProperties?.serviceInstalled ? (
        <Button
          className="rounded m-2 addChannel-btn"
          onClick={() => {
            const textSmsUrl = "/channels/textsms?";
            if (chatView) {
              window.open(textSmsUrl + channelRedirectUrl, "_blank");
            } else {
              navigate(textSmsUrl + channelRedirectUrl);
            }
          }}
          type=""
        >
          Add Channels
        </Button>
      ) : null}
    </div>
  );
};

const ListView = React.memo(
  ({
    numberList,
    setNumbersList,
    selectedContact,
    handleNumberSelect,
    setIsChannelPresent,
    setSelectedContact,
    setFilterContact,
    appProperties,
    services,
    selectedContactId,
    showConversations,
    setShowConversations,
    setShowDescription,
    showDescription,
    newToNumberInputRef,
    setShowChatOverlay,
    setAppProperties,
    setImportContactsNotificationOpen,
    setContactsAvailable,
    setSelectedService,
    selectedService,
    setContactsLoaded,
    setShowModalOverChat,
    greaterThanLargeScreen
  }) => {
    const listRef = useRef(null);
    const { setConversationContext } = useContext(ConvContext);
    const { fetchData } = useHttp();
    const [searchInputLoader, setSearchInputLoader] = useState(null);
    const [filterSearchInput, setFilterSearchInput] = useState("");
    const [searchNoContactContent, setSearchNoContactContent] = useState(false);
    const [controller, setController] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [rolesProperties] = useRolesContext();
    const hasInboxEditAccess = useMemo(() => {
      return checkUserHasEditAccess(rolesProperties?.currentRoleDetails, "Inbox");
    }, [rolesProperties]);
    const location = useLocation();
    const navigate = useNavigate();
    const { contactId: conatctIdFromUrl } = useParams();

    const {
      activeTab,
      setActiveTab,
      selectedChannels,
      setSelectedChannels,
      selectedFlagFilter,
      setSelectedFlagFilter,
      selectedAssignees,
      setSelectedAssignees,
      selectedReadStatus,
      setSelectedReadStatus,
      selectedConversationStatus,
      setSelectedConversationStatus,
      selectedDateRange,
      setSelectedDateRange,
      searchBy: storeSearchBy,
      setSearchBy: setStoreSearchBy,
      resetFilters,
    } = useListViewStore();
    const [flagValue, setFlagValue] = useState(false);
    const paginationRef = useRef();
    const { Text } = Typography;
    const [popoverOpen, setPopoverOpen] = useState(false);

    // Tab-to-Filter Configuration
    const tabFilterConfig = React.useMemo(
      () => ({
        all: { disabledFilters: [] },
        unread: {
          disabledFilters: ["readStatus"],
          readStatus: "unread",
        },
      }),
      []
    );

    // Sync query param to activeTab state
    useEffect(() => {
      const inboxViewFromQueryParam = searchParams.get("hs-inbox-view");

      if (inboxViewFromQueryParam && INBOX_DEFAULT_FILTERS_VALUES.includes(inboxViewFromQueryParam)) {
        if (inboxViewFromQueryParam !== activeTab) {
          setActiveTab(INBOX_DEFAULT_FILTERS[inboxViewFromQueryParam].value || "all");
        }
      } else if (!inboxViewFromQueryParam) {
        // If no query param, default to "all" tab
        if (activeTab !== "all") {
          setActiveTab("all");
        }
      }
    }, [searchParams, activeTab, setActiveTab]);

    const clearContactFilters = () => {
      setFilterSearchInput("");
      setFilterContact("");
      setSearchNoContactContent(false);
      fetchApi(""); // Pass empty string to clear search
    };

    const clearContactFiltersRef = useRef(clearContactFilters);
    clearContactFiltersRef.current = clearContactFilters;

    // Clear search when we landed here via Notification panel (navigation carries state)
    useEffect(() => {
      if (location.state?.fromNotification) {
        clearContactFiltersRef.current();
        navigate(location.pathname + (location.search || ""), { replace: true, state: {} });
      }
    }, [location.state?.fromNotification, location.pathname, location.search, navigate]);

    // Helper function to handle flag changes in the list view
    const handleListViewFlagChange = (contactId, newFlag) => {
      setNumbersList((prevList) => {
        return prevList.map((item) => {
          if (item.contactId === contactId) {
            const updatedUserFlagColorMap = {
              ...(item.userFlagColorMap || {}),
              [appProperties?.currentUser?.osyncUserId]: newFlag,
            };

            // Remove the key if flag is null
            if (!newFlag) {
              delete updatedUserFlagColorMap[appProperties?.currentUser?.osyncUserId];
            }

            return {
              ...item,
              userFlagColorMap: updatedUserFlagColorMap,
            };
          }
          return item;
        });
      });

      // If this contact is currently selected, also update the conversationContext
      if (selectedContactId.current === contactId) {
        setConversationContext((prev) => {
          const currentContact = prev[contactId] || {};
          const updatedUserFlagColorMap = {
            ...(currentContact.userFlagColorMap || {}),
            [appProperties?.currentUser?.osyncUserId]: newFlag,
          };

          if (!newFlag) {
            delete updatedUserFlagColorMap[appProperties?.currentUser?.osyncUserId];
          }
          return {
            ...prev,
            [contactId]: {
              ...currentContact,
              userFlagColorMap: updatedUserFlagColorMap,
            },
          };
        });
      }
    };

    const fetchApi = (searchText = filterSearchInput) => {
      setSearchInputLoader(<div className="actionIconsSprite dropDownLoading" />);
      if (controller) controller.abort();
      const controllerObj = new AbortController();
      setController(controllerObj);
      let contactCount = numberList?.length;

      // Get tab config for current tab
      const tabConfig = tabFilterConfig[activeTab] || tabFilterConfig.all;

      // Build query params for multi-select filters
      const queryParams = [];

      // Search text
      if (searchText) {
        queryParams.push("searchText=" + encodeURIComponent(searchText));
      }

      // Process channels (multi-select)
      const serviceIds = [];
      const orgMembers = [];
      const associatedTeams = [];

      // For "my-open" tab, use current user as assignee, otherwise use selectedAssignees
      const effectiveAssignees =
        activeTab === "my-open"
          ? [appProperties?.currentUser?.osyncUserId].filter(Boolean)
          : selectedAssignees && selectedAssignees.length > 0
            ? selectedAssignees
            : [];

      // Use tab config readStatus if tab is active, otherwise use selectedReadStatus
      const effectiveReadStatus = activeTab === INBOX_DEFAULT_FILTERS.unread.value && tabConfig.readStatus ? tabConfig.readStatus : selectedReadStatus || "all";

      // Process selected channels
      if (selectedChannels && selectedChannels.length > 0) {
        selectedChannels.forEach((channel) => {
          if (channel === "all") {
            // Skip "all" option
            return;
          } else if (channel.includes("_phone___")) {
            const phoneNumber = channel.replace("_phone___", "");
            orgMembers.push(phoneNumber);
          } else if (channel.includes("_team___")) {
            const teamId = channel.replace("_team___", "");
            associatedTeams.push(teamId);
          } else {
            serviceIds.push(channel);
          }
        });
      }

      // Add serviceIds
      if (serviceIds.length > 0) {
        queryParams.push("serviceId=" + encodeURIComponent(serviceIds.join(",")));
      }

      // Add orgMembers
      if (orgMembers.length > 0) {
        queryParams.push("orgMember=" + encodeURIComponent(orgMembers.join(",")));
      }

      // Add associatedTeams
      if (associatedTeams.length > 0) {
        queryParams.push("associatedTeam=" + encodeURIComponent(associatedTeams.join(",")));
      }

      // Add assignees
      if (effectiveAssignees.length > 0) {
        queryParams.push("contactOwnerEntityId=" + encodeURIComponent(effectiveAssignees.join(",")));
      }
      // If "all", don't add unread parameter
      // Add unread boolean (map readStatus to unread boolean)
      // Unread is only handled via tabs, not as a conversation status checkbox
      if (effectiveReadStatus === "unread") {
        queryParams.push("unread=true");
      } else if (effectiveReadStatus === "read") {
        queryParams.push("unread=false");
      }

      // Add conversation status (open/close only - unread is handled via tabs)
      // For "my-open" tab, always use "open" status
      let conversationStatusFilter = [];
      if (activeTab === "my-open" || activeTab === "unread") {
        conversationStatusFilter = ["open"];
      } else {
        const conversationStatuses = Array.isArray(selectedConversationStatus) ? selectedConversationStatus : [];
        // Filter out "unread" as it's only handled via tabs
        conversationStatusFilter = conversationStatuses.filter((status) => status !== "unread");
      }
      if (conversationStatusFilter.length > 0) {
        queryParams.push("conversationStatusType=" + encodeURIComponent(conversationStatusFilter.join(",")));
      }

      // Add flag filters
      if (selectedFlagFilter && selectedFlagFilter.length > 0) {
        queryParams.push("flag=" + encodeURIComponent(selectedFlagFilter.join(",")));
      }

      // Add date range filters
      if (selectedDateRange && selectedDateRange.length === 2) {
        // Set start date to 00:00:00 in browser timezone, then convert to UTC
        const startDate = selectedDateRange[0].startOf("day").utc().toISOString();
        // Set end date to 23:59:59.999 in browser timezone, then convert to UTC
        const endDate = selectedDateRange[1].endOf("day").utc().toISOString();
        queryParams.push("startDate=" + encodeURIComponent(startDate));
        queryParams.push("endDate=" + encodeURIComponent(endDate));
      }

      const queryString = queryParams.length > 0 ? "&" + queryParams.join("&") : "";

      fetchData(`omessage/contacts?limit=100${queryString}`, "GET", null, appProperties, null, null, controllerObj.signal)
        .then(function (response) {
          if (response) {
            response = JSON.parse(response);
            setShowConversations(true);
            if (response?.data?.errorMessage) {
              setShowConversations(false);
              message.error(response?.data?.errorMessage);
              return;
            }
            let resp = response?.data?.contacts;
            paginationRef.current = response?.data?.paginationModel?.lastKey;
            setNumbersList(resp);
            setContactsAvailable((prev) => (prev === null ? resp?.length > 0 : prev));
            contactCount = resp?.length;
            setFilterContact(searchText);
            setSearchInputLoader(null);

            const hasFlagValue = response?.data?.contacts?.some((item) => Object.keys(item?.userFlagColorMap ?? {}).length > 0);
            setFlagValue(hasFlagValue);

            if (searchText && searchText.length > 0) {
              setSearchNoContactContent(true);
            } else {
              setSearchNoContactContent(false);
            }

            setShowDescription((prev) => {
              if (prev === true) {
                return false;
              }
              return prev;
            });
            if (response?.data?.importContactsModel?.recordsProcessed) {
              setConversationContext((prev) => ({
                ...prev,
                importedContacts: response?.data?.importContactsModel,
              }));
              setImportContactsNotificationOpen(true);
            }
          } else {
            setSearchInputLoader(null);
          }
          setSearchInputLoader(null);
        })
        .catch((err) => {
        setSearchInputLoader(null);
      })
        .finally(() => {
          setContactsLoaded(true);
          if (paginationRef.current && searchText && contactCount < 20) {
            debounceFetchData();
          }
        });
    };


    useEffect(() => {
      if (appProperties) {
        fetchApi();
      }
    }, [
      selectedChannels,
      setNumbersList,
      fetchData,
      setFilterContact,
      setShowConversations,
      appProperties?.companyId,
      activeTab,
      selectedFlagFilter,
      selectedAssignees,
      selectedReadStatus,
      selectedConversationStatus,
      selectedDateRange,
      tabFilterConfig,
    ]);

    const debounceFetchData = useDebouncedCallback(() => {
      if (controller) controller.abort();
      const controllerObj = new AbortController();
      setController(controllerObj);
      controller.abort();

      // Get tab config for current tab
      const tabConfig = tabFilterConfig[activeTab] || tabFilterConfig.all;

      // Build query params for multi-select filters
      const queryParams = [];

      // Pagination
      queryParams.push("lastKey=" + encodeURIComponent(paginationRef.current ?? ""));

      // Search text
      if (filterSearchInput !== "") {
        queryParams.push("searchText=" + encodeURIComponent(filterSearchInput));
      }

      // Process channels (multi-select)
      const serviceIds = [];
      const orgMembers = [];
      const associatedTeams = [];

      // For "my-open" tab, use current user as assignee, otherwise use selectedAssignees
      const effectiveAssignees =
        activeTab === "my-open"
          ? [appProperties?.currentUser?.osyncUserId].filter(Boolean)
          : selectedAssignees && selectedAssignees.length > 0
            ? selectedAssignees
            : [];

      // Use tab config readStatus if tab is active, otherwise use selectedReadStatus
      const effectiveReadStatus = activeTab === "unread" && tabConfig.readStatus ? tabConfig.readStatus : selectedReadStatus || "all";

      // Process selected channels
      if (selectedChannels && selectedChannels.length > 0) {
        selectedChannels.forEach((channel) => {
          if (channel === "all") {
            // Skip "all" option
            return;
          } else if (channel.includes("_phone___")) {
            const phoneNumber = channel.replace("_phone___", "");
            orgMembers.push(phoneNumber);
          } else if (channel.includes("_team___")) {
            const teamId = channel.replace("_team___", "");
            associatedTeams.push(teamId);
          } else {
            serviceIds.push(channel);
          }
        });
      }

      // Add serviceIds
      if (serviceIds.length > 0) {
        queryParams.push("serviceId=" + encodeURIComponent(serviceIds.join(",")));
      }

      // Add orgMembers
      if (orgMembers.length > 0) {
        queryParams.push("orgMember=" + encodeURIComponent(orgMembers.join(",")));
      }

      // Add associatedTeams
      if (associatedTeams.length > 0) {
        queryParams.push("associatedTeam=" + encodeURIComponent(associatedTeams.join(",")));
      }

      // Add assignees
      if (effectiveAssignees.length > 0) {
        queryParams.push("contactOwnerEntityId=" + encodeURIComponent(effectiveAssignees.join(",")));
      }

      // Add unread boolean (map readStatus to unread boolean)
      // Unread is only handled via tabs, not as a conversation status checkbox
      if (effectiveReadStatus === "unread") {
        queryParams.push("unread=true");
      } else if (effectiveReadStatus === "read") {
        queryParams.push("unread=false");
      }
      // If "all", don't add unread parameter

      // Add conversation status (open/close only - unread is handled via tabs)
      // For "my-open" tab, always use "open" status
      let conversationStatusFilter = [];
      if (activeTab === "my-open" || activeTab === "unread") {
        conversationStatusFilter = ["open"];
      } else {
        const conversationStatuses = Array.isArray(selectedConversationStatus) ? selectedConversationStatus : [];
        // Filter out "unread" as it's only handled via tabs
        conversationStatusFilter = conversationStatuses.filter((status) => status !== "unread");
      }
      if (conversationStatusFilter.length > 0) {
        queryParams.push("conversationStatusType=" + encodeURIComponent(conversationStatusFilter.join(",")));
      }

      // Add flag filters
      if (selectedFlagFilter && selectedFlagFilter.length > 0) {
        queryParams.push("flag=" + encodeURIComponent(selectedFlagFilter.join(",")));
      }

      // Add date range filters
      if (selectedDateRange && selectedDateRange.length === 2) {
        // Set start date to 00:00:00 in browser timezone, then convert to UTC
        const after = selectedDateRange[0].startOf("day").utc().toISOString();
        // Set end date to 23:59:59.999 in browser timezone, then convert to UTC
        const before = selectedDateRange[1].endOf("day").utc().toISOString();
        queryParams.push("after=" + encodeURIComponent(after));
        queryParams.push("before=" + encodeURIComponent(before));
      }

      const queryString = queryParams.length > 0 ? "&" + queryParams.join("&") : "";

      fetchData("omessage/contacts?limit=100" + queryString, "GET", null, appProperties, null, null, controllerObj.signal)
        .then(function (response) {
          try {
            if (response) {
              response = JSON.parse(response);
              let resp = response?.data?.contacts;
              paginationRef.current = response.data?.paginationModel?.lastKey;
              setNumbersList((prev) => [...(prev || []), ...resp]);
            } else {
              paginationRef.current = null;
            }
          } catch (error) {
            console.error("Error processing response:", error);
          }
        })
        .catch((err) => {
        paginationRef.current = null;
      })
        .finally(() => {
          if (paginationRef.current && filterSearchInput && numberList?.length < 20) {
            debounceFetchData();
          }
        });
    }, 500);

    const handleScroll = async () => {
      const element = listRef.current;
      if (element.scrollTop + element.clientHeight >= element.scrollHeight) {
        if (paginationRef.current) {
          debounceFetchData();
        }
      }
    };

    const handleUpdateInfo = (resp, customerDisplayNameObject) => {
      const {
        sourceFrom,
        contactOwnerEntityId,
        firstName,
        unreadCount,
        integId,
        orgMember,
        orgCustomer,
        contactId,
        avatar,
        sourceModule,
        sourceId,
        lastName,
        rightServiceId,
        userFlagColorMap,
        conversationStatus,
        conversationStatusType,
      } = resp;
      setNumbersList((prevRecords) => {
        const index = prevRecords?.findIndex((obj) => obj?.orgMember === orgMember && obj?.orgCustomer === orgCustomer);
        if (index !== -1) {
          const updatedRecords = [...prevRecords];
          updatedRecords[index] = {
            ...updatedRecords[index],
            unreadCount: null,
          };
          return updatedRecords;
        }
        return prevRecords;
      });
      selectedContactId.current = contactId || DEFAULT_CONTACT_ID;

      setConversationContext((prev) => ({
        ...prev,
        [selectedContactId.current]: {
          ...(prev[contactId || DEFAULT_CONTACT_ID] || {}),
          displayName: sourceFrom,
          avatar: avatar,
          rightServiceId: rightServiceId,
          firstName: firstName || lastName,
          lastName: lastName,
          unreadCount: unreadCount,
          integId: integId,
          sourceModule: sourceModule,
          sourceId: sourceId,
          userFlagColorMap: userFlagColorMap,
          contactAvatar: getContactAvatar(resp, customerDisplayNameObject, appProperties),
          contactOwnerEntityId: contactOwnerEntityId,
          conversationStatus: conversationStatus,
          conversationStatusType: conversationStatusType,
          id: contactId,
        },
      }));
    };

    const fetchContactsWithQuery = () => {
      setIsChannelPresent(false);
      setSelectedContact(null);
      selectedContactId.current = null;
      fetchApi();
    };

    useEffect(() => {
      if (
        numberList?.length > 0 &&
        selectedContactId.current === DEFAULT_CONTACT_ID &&
        urlParams.get("contactId") &&
        urlParams.get("service") === "teams"
      ) {
        var contact = numberList.find((obj) => obj?.contactId === urlParams.get("contactId"));
        if (contact) {
          selectedContactId.current = contact?.contactId;
          const selectedNumbers = {
            fromNumber: contact?.orgMember,
            toNumber: contact?.orgCustomer,
            integId: contact?.integId,
          };
          const customerDisplayNameObject = getDisplayNameOfCustomer(contact);
          handleNumberSelect("", selectedNumbers, contact);
          handleUpdateInfo(contact, customerDisplayNameObject);
        }
      }
    }, [numberList]);

    const ListViewDescription = ({ contactObject = {} }) => {
      const hasUnreadCount =
        contactObject?.unreadCount > 0 &&
        !(selectedContact?.toNumber === contactObject.orgCustomer && selectedContact?.fromNumber === contactObject?.orgMember);
      return (
        <Row justify={"space-between"} style={{ color: "#68677F" }} wrap={false}>
          <Col flex="auto" className="d-flex align-items-center hs-fs-13">
            <span className="me-1">
              {contactObject?.conversationStatus?.includes("close") ? <div className="actionIconsSprite2 conversation-closed-icon"></div> : contactObject?.messageTagStatus === "queued" ? (
                <div className="actionIconsSprite contact-queue-icon"></div>
              ) : contactObject?.messageTagStatus === "sent" ? (
                <div className="actionIconsSprite sent-icon"></div>
              ) : ["failed", "undelivered"].includes(contactObject?.messageTagStatus) ? (
                <div className="actionIconsSprite contact-failure-icon"></div>
              ) : contactObject?.messageTagStatus === "delivered" ? (
                <div className="actionIconsSprite contact-delivered-icon"></div>
              ) : contactObject?.messageTagStatus === "read" ? (
                <div className="actionIconsSprite read-icon "></div>
              ) : null}
            </span>
            {contactObject?.conversationStatus?.includes("close") ? <></> : <span
              className={
                contactObject?.messageTagType &&
                  !contactObject.messageTagType.includes("dummy") &&
                  !contactObject.messageTagType.includes("text")
                  ? `me-1 actionIconsSprite contact-${contactObject.messageTagType.includes("gif")
                    ? "gif"
                    : contactObject.messageTagType.includes("video")
                      ? "video"
                      : contactObject.messageTagType.includes("image")
                        ? "image"
                        : "file"
                  }-icon`
                  : ""
              }
              data-cy={
                contactObject?.messageTagType &&
                  !contactObject.messageTagType.includes("dummy") &&
                  !contactObject.messageTagType.includes("text")
                  ? `contact-${contactObject.messageTagType}-icon`
                  : undefined
              }
            ></span>}

            <Text ellipsis style={{ minWidth: "50%" }} className="hs-color-dark-grey-2">
              {contactObject?.conversationStatusType === appProperties?.enums?.conversationStatusType?.CLOSED.code ? appProperties?.enums?.conversationStatusType?.CLOSED?.activeLabel : contactObject.messageTag ? (
                <>{contactObject.messageTag}</>
              ) : contactObject?.messageTagType?.includes("gif") ? (
                "GIF"
              ) : contactObject?.messageTagType?.includes("video") ? (
                "Video"
              ) : contactObject?.messageTagType?.includes("image") ? (
                "Image"
              ) : (
                contactObject?.messageTagType && !contactObject?.messageTagType?.includes("dummy") && "File"
              )}
            </Text>
          </Col>
          {hasUnreadCount && (
            <Col flex="35px">
            <Tag className="text-white hs-bg-green-1 border-none ms-auto hs-border-9999-px" data-cy="unread-count">
              {contactObject?.unreadCount > 99 ? "99+" : contactObject?.unreadCount}
            </Tag>
          </Col>
          )}
          {contactObject.new && (
            <Col>
              <Tag
                data-cy="new-contact-tag"
                className="rounded-5 border-0 h-100"
                style={{
                  backgroundColor: "#FEF5EA",
                  color: "#1C1D22",
                }}
              >
                {contactObject.new}
              </Tag>
            </Col>
          )}
        </Row>
      );
    };

    return (
      <div className="bg-white d-flex flex-column h-100">
        <>
          <Row className="px-3 pt-2 d-flex justify-content-between align-items-center inbox-header-conversation">
            <Col
              className="d-flex align-items-center"
              style={{
                fontSize: 16,
                fontWeight: 600,
                paddingTop: 3,
              }}
            >
              <span style={{ cursor: "default" }}>Conversations</span>
            </Col>
            {services?.length > 0 && (
              <Col>
                <div className="hs-h-30-px hs-w-30-px">
                  <HSButton
                    size="s"
                    type="primary"
                    buttonClassName="w-100 h-100 hs-circle-button"
                    data-cy="initiate-new-conversation-plus-icon"
                    onClick={() => {
                      setShowModalOverChat(true);
                      newToNumberInputRef?.current?.focus();
                    }}
                    prefixIcon={<div className="actionIconsSprite plusIconWhite" />}
                  />
                </div>
              </Col>
            )}
          </Row>
          <div className="d-flex flex-shrink-0 gap-2 align-items-center justify-content-between px-3 inbox-contact-search">
            <Input
              placeholder="Search"
              data-cy="inbox-search-area"
              rootClassName="h-75 hs-max-h-40-px"
              className="hs-input-box"
              size="medium"
              prefix={<div className="actionIconsSprite search-icon" />}
              suffix={
                <div className="h-100 d-flex align-items-center">
                  {filterSearchInput?.length > 0 && (
                    <div className="d-flex align-items-center">
                      <div className="actionIconsSprite clear-search" data-cy="cancel-search-btn" onClick={clearContactFilters} />
                    </div>
                  )}
                </div>
              }
              tabIndex={1}
              value={filterSearchInput}
              onChange={(e) => {
                setFilterSearchInput(e.target.value);
                setIsChannelPresent(false);
                setSelectedContact(null);
                selectedContactId.current = DEFAULT_CONTACT_ID;
                //check if contactId is present in the url pathname if found remove it
                if (conatctIdFromUrl) {
                  deselectCurrentContact(navigate, location);
                }
                if (e?.target?.value?.length === 0) {
                  fetchApi("");
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchContactsWithQuery();
                }
              }}
            />
            <Popover
              arrow={false}
              fresh
              content={
                <InboxFilterPopoverCard
                  services={services}
                  setIsChannelPresent={setIsChannelPresent}
                  setSelectedContact={setSelectedContact}
                  selectedContactId={selectedContactId}
                  setPopoverOpen={setPopoverOpen}
                  popoverOpen={popoverOpen}
                />
              }
              title={<span className="hs-text hs-fw-500">Filter Conversations</span>}
              trigger="click"
              autoAdjustOverflow
              open={popoverOpen}
              onOpenChange={setPopoverOpen}
              overlayClassName={`hs-w-${greaterThanLargeScreen ? "400" : "350"}-px inbox-filter-popover rounded-1 border`}
              overlayInnerStyle={{ padding: "16px 16px" }}
              placement="bottomLeft"
            >
              <div className="actionIconsSprite2 inbox-filter-icon m-auto hs-input-box h-75 hs-max-h-40-px" data-cy="inbox-filter-icon" />
            </Popover>
          </div>
          {/* Active Filters Section - hide filters that are disabled for the current tab */}
          {(() => {
            const disabledFilters = INBOX_DEFAULT_FILTERS[activeTab]?.disabledFilters || [];
            const hasVisibleFilters =
              (selectedChannels && selectedChannels.length > 0) ||
              (selectedFlagFilter && selectedFlagFilter.length > 0) ||
              (selectedDateRange && selectedDateRange.length === 2) ||
              (!disabledFilters.includes("status") && selectedConversationStatus && selectedConversationStatus.length > 0) ||
              (!disabledFilters.includes("assignee") && selectedAssignees && selectedAssignees.length > 0);
            return hasVisibleFilters;
          })() && (
              <div data-cy="active-filters-section" className="px-3 py-2" style={{ borderBottom: "1px solid #f0f0f0" }}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="hs-fs-12 hs-fw-500 hs-color-dark-grey">Active Filters</span>
                  <div
                    className="hs-fs-12 hs-link"
                    onClick={() => {
                      resetFilters();
                      setStoreSearchBy("serviceId");
                      setIsChannelPresent(false);
                      setSelectedContact(null);
                      selectedContactId.current = DEFAULT_CONTACT_ID;
                      if (conatctIdFromUrl) {
                        deselectCurrentContact(navigate, location);
                      }
                    }}
                  >
                    Clear All
                  </div>
                </div>
                <div data-cy="active-filters" className="d-flex flex-wrap gap-1">
                  {(() => {
                    // Helper function to get channel display name from services tree
                    const getChannelDisplayName = (channelValue) => {
                      if (channelValue === "all") return "";

                      // Search through services and their children
                      for (const service of services) {
                        // Check if it's a service match
                        if (service.value === channelValue) {
                          return service.title || channelValue;
                        }

                        // Check children (phone numbers and teams)
                        if (service.children) {
                          for (const child of service.children) {
                            if (child.value === channelValue) {
                              // Title is already a string in the services array
                              return child.title || channelValue;
                            }
                          }
                        }
                      }

                      // Fallback if not found
                      return channelValue;
                    };

                    // Build array of active filters (exclude filters disabled for current tab)
                    const disabledFilters = INBOX_DEFAULT_FILTERS[activeTab]?.disabledFilters || [];
                    const activeFilters = [];

                    // Add channel filters if selected (multi-select)
                    if (selectedChannels && selectedChannels.length > 0) {
                      selectedChannels.forEach((channel) => {
                        if (channel === "all") return;

                        const channelDisplayName = getChannelDisplayName(channel);

                        activeFilters.push({
                          id: `channel-${channel}`,
                          label: channelDisplayName,
                          onClose: () => {
                            const updatedChannels = selectedChannels.filter((c) => c !== channel);
                            setSelectedChannels(updatedChannels);
                            setIsChannelPresent(false);
                            setSelectedContact(null);
                            selectedContactId.current = DEFAULT_CONTACT_ID;
                          },
                        });
                      });
                    }

                    // Add conversation status filters if selected (skip when disabled for tab, e.g. unread)
                    if (!disabledFilters.includes("status") && selectedConversationStatus && selectedConversationStatus.length > 0) {
                      // Filter out "unread" as it's only handled via tabs
                      const conversationStatusFilters = selectedConversationStatus.filter((status) => status !== "unread");
                      conversationStatusFilters.forEach((status) => {
                        activeFilters.push({
                          id: `conversationStatus-${status}`,
                          label: status.charAt(0).toUpperCase() + status.slice(1),
                          onClose: () => {
                            const updatedStatuses = selectedConversationStatus.filter((s) => s !== status);
                            setSelectedConversationStatus(updatedStatuses);
                            setIsChannelPresent(false);
                            setSelectedContact(null);
                            selectedContactId.current = DEFAULT_CONTACT_ID;
                          },
                        });
                      });
                    }

                    // Add flag filters if selected
                    if (selectedFlagFilter && selectedFlagFilter.length > 0) {
                      selectedFlagFilter.forEach((flag) => {
                        activeFilters.push({
                          id: `flag-${flag}`,
                          label: flag.charAt(0).toUpperCase() + flag.slice(1),
                          onClose: () => {
                            const updatedFlags = selectedFlagFilter.filter((f) => f !== flag);
                            setSelectedFlagFilter(updatedFlags);
                            setIsChannelPresent(false);
                            setSelectedContact(null);
                            selectedContactId.current = DEFAULT_CONTACT_ID;
                          },
                        });
                      });
                    }

                    // Add assignee filters if selected (skip when disabled for tab, e.g. my-open, my-assigned)
                    if (!disabledFilters.includes("assignee") && selectedAssignees && selectedAssignees.length > 0) {
                      selectedAssignees.forEach((assigneeId) => {
                        const assigneeUser = appProperties?.licenseObj?.allLicensedUserDetails?.find((user) => user.osyncUserId === assigneeId);
                        const assigneeDisplayName =
                          assigneeUser.firstName || assigneeUser.lastName
                            ? `${assigneeUser.firstName || ""} ${assigneeUser.lastName || ""} (${assigneeUser.email})`.trim()
                            : assigneeUser.email;
                        activeFilters.push({
                          id: `assignee-${assigneeId}`,
                          label: assigneeDisplayName,
                          onClose: () => {
                            const updatedAssignees = selectedAssignees.filter((a) => a !== assigneeId);
                            setSelectedAssignees(updatedAssignees);
                            setIsChannelPresent(false);
                            setSelectedContact(null);
                            selectedContactId.current = DEFAULT_CONTACT_ID;
                          },
                        });
                      });
                    }

                    // Add date range filter if selected
                    if (selectedDateRange && selectedDateRange.length === 2) {
                      const dateRangeLabel = `${formatDate(selectedDateRange[0])} - ${formatDate(selectedDateRange[1])}`;
                      activeFilters.push({
                        id: "dateRange",
                        label: dateRangeLabel,
                        onClose: () => {
                          setSelectedDateRange(null);
                          setIsChannelPresent(false);
                          setSelectedContact(null);
                          selectedContactId.current = DEFAULT_CONTACT_ID;
                        },
                      });
                    }

                    // Render all filters in a single loop
                    return activeFilters?.map?.((filter) => (
                      <Tag
                        key={filter?.id}
                        className="p-1 px-2 my-1 hs-border-15 hs-bg-tag-purple d-flex align-items-center hs-fw-500 user-select-tab hs-break-all"
                        closable
                        onClose={filter?.onClose || (() => {})}
                      >
                        {formatPhoneNumber(filter?.label)}
                      </Tag>
                    ));
                  })()}
                </div>
              </div>
            )}

          <div className="d-flex flex-shrink-0 align-items-center justify-content-between">
            <Tabs
              data-cy="tabs-container"
              defaultActiveKey="all"
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                // Clear filters that are disabled for the selected tab
                const tabDisabledFilters = INBOX_DEFAULT_FILTERS[key]?.disabledFilters || [];
                if (tabDisabledFilters.includes("status")) {
                  setSelectedConversationStatus([]);
                }
                if (tabDisabledFilters.includes("assignee")) {
                  setSelectedAssignees([]);
                }
                // Clear read status filter when switching to "unread" tab
                if (key === "unread") {
                  setSelectedReadStatus("all");
                }
                setIsChannelPresent(false);
                setSelectedContact(null);
                selectedContactId.current = DEFAULT_CONTACT_ID;
                // Update query param, preserving other query params
                const newSearchParams = new URLSearchParams(searchParams);
                if (key === "all") {
                  newSearchParams.delete("hs-inbox-view");
                } else {
                  newSearchParams.set("hs-inbox-view", key);
                }
                setSearchParams(newSearchParams, { replace: true });
                if (conatctIdFromUrl) {
                  navigate({ pathname: "/inbox", search: newSearchParams.toString() }, { replace: true });
                }
              }}
              className="custom-tabs custom-tabs-bg custom-tabs-align"
              style={{ flex: 1 }}
              items={[
                {
                  key: "all",
                  label: <span data-cy="tab-all">All</span>,
                },
                {
                  key: "unread",
                  label: <span data-cy="tab-unread">Unread</span>,
                },
                {
                  key: "my-open",
                  label: <span data-cy="tab-my-open">My Open</span>,
                },
              ]}
            />
          </div>
          {showConversations ? (
            searchInputLoader ? (
              <ConversationListSkeletonLoading />
            ) : (
              <>
                {numberList?.length > 0 ? (
                  <div
                    ref={listRef}
                    className={`hs-h-84 hs-default-list-scroll flex-grow-* overflow-auto`}
                    data-cy="contacts-list-to-view-conversations"
                    style={{ scrollBehavior: "auto" }}
                    onScroll={handleScroll}
                  >
                    <List
                      header={null}
                      footer={null}
                      dataSource={numberList}
                      className="chatList h-100"
                      rootClassName="h-100"
                      renderItem={(resp, index) => {
                        const customerDisplayNameObject = getDisplayNameOfCustomer(resp);
                        return (
                          <List.Item
                            tabIndex={index + 4}
                            value={resp.timeInMillis}
                            messagedate={formatDate(resp.messageTime)} // used for cypress tests to verify the conversation date
                            onClick={() => {
                              if (resp?.contactId === selectedContactId.current) {
                                return;
                              }
                              selectedContactId.current = resp?.contactId;
                              const selectedNumbers = {
                                fromNumber: resp?.orgMember,
                                toNumber: resp?.orgCustomer,
                                integId: resp?.integId,
                              };
                              setConversationContext((prev) => ({
                                ...prev,
                                [selectedContactId.current]: {
                                  ...prev[selectedContactId.current],
                                  conversations: [],
                                },
                              }));
                              handleNumberSelect(resp?.lastName, selectedNumbers, resp);
                              handleUpdateInfo(resp, customerDisplayNameObject);
                            }}
                            className="conv-list-item px-1 hs-border-dark-grey-bottom"
                            data-cy="conversation-number-list"
                            style={{
                              backgroundColor:
                                selectedContactId.current === resp.contactId ||
                                  (!selectedContactId.current && selectedContact?.toNumber === resp.orgCustomer && resp.new)
                                  ? "rgba(245, 245, 255, 1)"
                                  : null,
                              padding: "12px 0px",
                            }}
                          >
                            <List.Item.Meta
                              className="align-items-center pe-2"
                              avatar={getContactAvatar(resp, customerDisplayNameObject, appProperties)}
                              title={
                                <Row wrap={false}>
                                  <Col flex="auto">
                                    <Flex wrap align="center" gap={4} justify="space-between">
                                      <Text ellipsis style={{ fontSize: 15 }} className="hs-text">
                                        {customerDisplayNameObject?.displayName}
                                      </Text>
                                      <div className="hs-color-light-grey-2 hs-fs-11 text-nowrap">
                                        {formatDate(resp.messageTime) === "Today"
                                          ? formatTime(resp.messageTime)
                                          : resp.new
                                            ? dayjs().format("hh:mm A")
                                            : formatDate(resp.messageTime)}
                                      </div>
                                    </Flex>
                                  </Col>
                                  <Col
                                    flex="25px"
                                    className="d-flex align-items-center justify-content-end gap-2 ms-auto hs-color-light-grey-2 hs-fw-400"
                                  >
                                    {appProperties?.currentUser?.osyncUserId && resp.contactId !== "new_contact" && !resp.new && (
                                      <div onClick={(e) => e.stopPropagation()}>
                                        <FlagSelector
                                          disabled={!hasInboxEditAccess}
                                          contactId={resp.contactId}
                                          currentFlag={resp?.userFlagColorMap?.[appProperties.currentUser.osyncUserId]}
                                          onFlagChange={(newFlag) => handleListViewFlagChange(resp.contactId, newFlag)}
                                          appProperties={appProperties}
                                          contactFlagOutline={false}
                                        />
                                      </div>
                                    )}
                                  </Col>
                                </Row>
                              }
                              description={<ListViewDescription contactObject={resp} />}
                            />
                          </List.Item>
                        );
                      }}
                    />
                  </div>
                ) : (
                  <div className="d-flex m-auto flex-column justify-content-center align-items-center hs-w-95">
                    {!searchNoContactContent ? (
                      <>
                        <div className="cardsSprite inboxNumberListEmpty-icon"></div>
                        <span className="p-2" style={{ color: "#606060" }}>
                          {!selectedFlagFilter && (!selectedChannels || selectedChannels.length === 0)
                            ? activeTab === "unread"
                              ? "You're all caught up!"
                              : activeTab === "my-open"
                                ? "No active assignments"
                                : "Your inbox is empty"
                            : "Filtered View is Empty"}
                        </span>
                        <span style={{ color: "#8B8D97", textAlign: "center" }}>
                          {!selectedFlagFilter && (!selectedChannels || selectedChannels.length === 0)
                            ? activeTab === "unread"
                              ? "New unread messages will show up here."
                              : activeTab === "my-open"
                                ? "Assigned conversations will show up here"
                                : "New message will show up here."
                            : "Tag conversations to organize your inbox and quickly find what matters."}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="p-2" style={{ color: "#606060" }}>
                          No matches found.
                        </span>
                        <span style={{ color: "#8B8D97" }}>
                          <span className="" data-cy="clear-search-btn" style={{ cursor: "pointer" }} onClick={clearContactFilters}>
                            Clear
                          </span>
                        </span>
                      </>
                    )}
                  </div>
                )}
              </>
            )
          ) : (
            <ConversationListSkeletonLoading />
          )}
        </>
      </div>
    );
  }
);

const formatDate = (date) => {
  const today = dayjs().format("YYYY-MM-DD");
  const yesterday = dayjs().subtract(1, "days").format("YYYY-MM-DD");
  if (date) {
    date = dayjs(date).format("YYYY-MM-DD");
    if (date === today) {
      return "Today";
    } else if (date === yesterday) {
      return "Yesterday";
    } else {
      return dayjs(date).format("D MMM, YYYY");
    }
  }
};

const convertURLsToLinks = (obj, isMMS) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = obj?.message?.split(urlRegex);
  return parts?.map((part, index) => {
    if (part?.match(urlRegex)) {
      return (
        <a
          // style={{
          //   color: obj?.direction === 2 && !isMMS && !["failed", "undelivered"].includes(obj?.messageStatus || obj?.status) ? "#fff" : "black",
          // }}
          key={index}
          href={part}
          target="_blank"
          rel="noreferrer"
        >
          {part}
        </a>
      );
    } else {
      return part;
    }
  });
};
